/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dashboardApi, projectsApi, tasksApi, requirementsApi, testsApi, milestonesApi, documentsApi, sprintsApi, budgetsApi, timeApi, projectExportApi, clientsApi, servicesApi, formatApiError, auditApi, googleCalendarApi, taskPlanningApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/ConfigContext';
import { adminApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ProjectMeetingsPanel from '../components/ProjectMeetingsPanel';
import TaskResourcePlanningPanel from '../components/TaskResourcePlanningPanel';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft, Folders, Target, Clock, TestTube, ListChecks, Flag,
  FileText, Users, CheckCircle, Warning, Upload, Trash, Plus,
  CalendarBlank, Download, FilePdf, FileXls, ArrowsHorizontal, PencilSimple, LinkSimple, GlobeSimple
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { format, addDays, differenceInDays, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatDate, getLabel, getBadgeClass, getTaskStatusColor, calculateProgress, getProgressColor, getInitials, formatTimeHundredths, toNoonUTC } from '../lib/utils';
import { toast } from 'sonner';

// REGLE DE GESTION : les horaires saisis pour une reunion sont interpretes dans le fuseau local
// du navigateur puis envoyes en ISO a Google Agenda.
const toDateTimeLocalInput = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const stripRequirementHtml = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

// REGLE DE GESTION V4.1 : l'editeur rapide projet produit du HTML, mais la
// sanitisation de securite reste exclusivement realisee par le backend.
function ProjectRequirementEditor({ value = '', onChange }) {
  const editorRef = useRef(null);
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);
  const command = (name, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(name, false, arg);
    onChange(editorRef.current?.innerHTML || '');
  };
  return (
    <div className="rounded-lg border bg-white overflow-hidden">
      <div className="flex flex-wrap gap-1 border-b bg-slate-50 p-1.5">
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 font-bold" onClick={() => command('bold')}>B</Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 italic" onClick={() => command('italic')}>I</Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 underline" onClick={() => command('underline')}>U</Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => command('formatBlock', 'h3')}>Titre</Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => command('insertUnorderedList')}>• Liste</Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => command('insertOrderedList')}>1. Liste</Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => { const url = window.prompt('Adresse du lien (https://...)'); if (url?.startsWith('http')) command('createLink', url); }}>Lien</Button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || '')}
        className="min-h-[110px] p-3 text-sm outline-none prose prose-sm max-w-none"
      />
    </div>
  );
}

const FALLBACK_TASK_STATUSES = [
  { value: 'todo', label: 'À faire', color: '#64748b' },
  { value: 'analysis', label: 'Analyse', color: '#0284c7' },
  { value: 'development', label: 'Développement', color: '#7c3aed' },
  { value: 'deliverable', label: 'Livrable', color: '#ea580c' },
  { value: 'testing', label: 'Recette', color: '#eab308' },
  { value: 'production', label: 'Production', color: '#16a34a' },
  { value: 'closed', label: 'Clôturé', color: '#059669' },
];

function MultiUserSelect({ users, selectedIds, onChange, placeholder = "Assigner..." }) {
  const selected = users.filter(u => selectedIds.includes(u.id));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start text-left font-normal h-auto min-h-[36px] py-1.5" data-testid="multi-user-select-project">
          {selected.length === 0 ? (
            <span className="text-muted-foreground text-sm">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selected.map(u => (
                <span key={u.id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                  {u.name.split(' ')[0]}
                </span>
              ))}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {users.map(u => (
            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
              <Checkbox
                checked={selectedIds.includes(u.id)}
                onCheckedChange={(checked) => {
                  if (checked) onChange([...selectedIds, u.id]);
                  else onChange(selectedIds.filter(id => id !== u.id));
                }}
              />
              <Avatar className="w-5 h-5">
                <AvatarFallback className="text-[8px] bg-blue-100 text-blue-700">{getInitials(u.name)}</AvatarFallback>
              </Avatar>
              <span className="text-sm">{u.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function ProjectDashboardPage() {
  const { getList } = useAppConfig();
  const documentCategories = getList('document_category');
  const budgetNatures = getList('budget_nature');
  const TASK_STATUSES = getList('task_status', FALLBACK_TASK_STATUSES);
  const PROJECT_STATUSES = getList('project_status', [{value:'draft',label:'Brouillon'},{value:'preparation',label:'En preparation'},{value:'in_progress',label:'En cours'},{value:'on_hold',label:'En attente'},{value:'closed',label:'Cloture'},{value:'archived',label:'Archive'}]);
  const REQUIREMENT_STATUSES = getList('requirement_status', [{value:'to_draft',label:'À rédiger'},{value:'to_analyze',label:'À analyser'},{value:'to_validate',label:'À valider'},{value:'validated',label:'Validée'},{value:'rejected',label:'Rejetée'},{value:'obsolete',label:'Obsolète'}]);
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [tests, setTests] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [googleStatus, setGoogleStatus] = useState({ configured: false, linked: false });
  const [googleMeetingOpen, setGoogleMeetingOpen] = useState(false);
  const [googleMeetingForm, setGoogleMeetingForm] = useState(() => {
    const start = new Date();
    start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    return { title: 'Réunion projet', start: toDateTimeLocalInput(start), end: toDateTimeLocalInput(end), description: '', attendees: '' };
  });

  // Create dialogs
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showReqDialog, setShowReqDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showEditProject, setShowEditProject] = useState(false);
  const [editProject, setEditProject] = useState({});
  const [closureCheck, setClosureCheck] = useState(null);
  const [closureLoading, setClosureLoading] = useState(false);
  const [editingReq, setEditingReq] = useState(null);
  const [editingMs, setEditingMs] = useState(null);

  // New items
  const [newTask, setNewTask] = useState({ title: '', description: '', status: 'todo', assigned_to: [], start_date: new Date(), end_date: addDays(new Date(), 7), estimated_days: '', parent_task_id: '', priority_level: 2, position: 1, sprint_id: '', dependencies: [], self_assignment_enabled: false });
  const [newReq, setNewReq] = useState({ title: '', description: '', description_html: '', type: 'functional', priority: 'medium' });
  const [newTest, setNewTest] = useState({ title: '', description: '', criticality: 'major', steps: [{ description: '', expected_result: '' }] });
  const [newMilestone, setNewMilestone] = useState({ name: '', description: '', target_date: new Date(), task_id: '' });

  // Budget & Material state
  const [budgetLines, setBudgetLines] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [budgetVentilation, setBudgetVentilation] = useState({rows:[],totals:{}});
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [newBudget, setNewBudget] = useState({ category: 'service', line_type: 'adjustment', status: 'draft', effective_date: new Date().toISOString().slice(0,10), label: '', adjustment_amount: '', estimated_amount: '0', engaged_amount: '0', consumed_amount: '0', forecast_amount: '', description: '' });
  const [budgetImporting, setBudgetImporting] = useState(false);
  const [budgetDocCategories, setBudgetDocCategories] = useState({});
  const [newMaterial, setNewMaterial] = useState({ name: '', category: 'serveur', quantity: 1, unit_cost: '', status: 'demande', description: '' });

  // Time entry state for project
  const [showTimeEntryDialog, setShowTimeEntryDialog] = useState(false);
  const [newTimeEntry, setNewTimeEntry] = useState({ task_id: '', activity: 'Developpement', date: new Date(), time_spent: '', description: '', billable: true });

  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    try {
      const [projRes, statsRes, tasksRes, reqsRes, testsRes, msRes, docsRes, spRes, usersRes, clientsRes, servicesRes] = await Promise.all([
        projectsApi.getById(projectId),
        dashboardApi.getProjectStats(projectId),
        tasksApi.getAll({ project_id: projectId }),
        requirementsApi.getAll({ project_id: projectId }),
        testsApi.getAll({ project_id: projectId }),
        milestonesApi.getAll({ project_id: projectId }),
        documentsApi.getByProject(projectId),
        sprintsApi.getAll({ project_id: projectId }),
        adminApi.getVisibleUsers().catch(() => ({ data: [] })),
        clientsApi.getAll().catch(() => ({ data: [] })),
        servicesApi.getAll().catch(() => ({ data: [] })),
      ]);
      setProject(projRes.data);
      setStats(statsRes.data);
      setTasks(tasksRes.data);
      setRequirements(reqsRes.data);
      setTests(testsRes.data);
      setMilestones(msRes.data);
      setDocuments(docsRes.data);
      setSprints(spRes.data);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
      try { const gs = await googleCalendarApi.status(); setGoogleStatus(gs.data || { configured: false, linked: false }); } catch { setGoogleStatus({ configured: false, linked: false }); }
      try { const hist = await auditApi.getProjectHistory(projectId); setHistory(hist.data || []); } catch { setHistory([]); }
      
      // Fetch budget & material data
      try {
        const [blRes, matRes, bsRes] = await Promise.all([
          budgetsApi.getLines(projectId),
          budgetsApi.getMaterials(projectId),
          budgetsApi.getSummary(projectId),
        ]);
        setBudgetLines(blRes.data);
        setMaterials(matRes.data);
        setBudgetSummary(bsRes.data);
        try { const vr = await budgetsApi.getVentilation(projectId); setBudgetVentilation(vr.data||{rows:[],totals:{}}); } catch { setBudgetVentilation({rows:[],totals:{}}); }
      } catch { /* budget endpoints may fail gracefully */ }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Drag state for GANTT
  const dragRef = useRef(null);
  const projectAvatarInputRef = useRef(null);
  const [dragInfo, setDragInfo] = useState(null);

  // GANTT data with date scale - moved before handleGanttMouseDown to avoid temporal dead zone
  const ganttData = useMemo(() => {
    if (!tasks.length) return { tasks: [], minDate: new Date(), maxDate: new Date(), totalDays: 1, dateCols: [] };
    const dates = tasks.flatMap(t => [new Date(t.start_date), new Date(t.end_date)]).filter(d => !isNaN(d));
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    // Add 1 day padding each side
    const padMin = new Date(min); padMin.setDate(padMin.getDate() - 1);
    const padMax = new Date(max); padMax.setDate(padMax.getDate() + 1);
    const totalDays = Math.max((padMax - padMin) / 86400000, 1);
    // Generate date columns (one per day, or per week if > 30 days)
    const dateCols = [];
    const stepDays = totalDays > 60 ? 7 : totalDays > 30 ? 3 : 1;
    let cursor = new Date(padMin);
    while (cursor <= padMax) {
      dateCols.push(new Date(cursor));
      cursor = new Date(cursor); cursor.setDate(cursor.getDate() + stepDays);
    }
    return { tasks, minDate: padMin, maxDate: padMax, totalDays, dateCols };
  }, [tasks]);

  // Parent tasks followed by their subtasks so the GANTT hierarchy stays readable.
  const hierarchicalTasks = useMemo(() => {
    const sortTask = (a,b) => (Number(a.priority_level ?? 99)-Number(b.priority_level ?? 99)) || (Number(a.position ?? 999)-Number(b.position ?? 999)) || String(a.start_date||'').localeCompare(String(b.start_date||''));
    const top = tasks.filter(t => !t.parent_task_id).sort(sortTask);
    const ordered = [];
    top.forEach(parent => {
      ordered.push(parent);
      tasks.filter(t => t.parent_task_id === parent.id).sort(sortTask).forEach(child => ordered.push(child));
    });
    tasks.filter(t => t.parent_task_id && !tasks.some(p => p.id === t.parent_task_id)).forEach(t => ordered.push(t));
    return ordered;
  }, [tasks]);

  // Create handlers
  const handleCreateTask = async () => {
    try {
      await tasksApi.create({
        ...newTask,
        project_id: projectId,
        start_date: toNoonUTC(newTask.start_date),
        end_date: toNoonUTC(newTask.end_date),
        parent_task_id: newTask.parent_task_id && newTask.parent_task_id !== 'none' ? newTask.parent_task_id : null,
        estimated_days: newTask.estimated_days ? parseFloat(newTask.estimated_days) : null,
        priority_level: parseInt(newTask.priority_level ?? 2),
        position: parseInt(newTask.position ?? 1),
        assigned_to: newTask.assigned_to.length > 0 ? newTask.assigned_to : [],
        sprint_id: newTask.sprint_id || null,
        dependencies: newTask.dependencies || [],
      });
      toast.success('Tache creee');
      setShowTaskDialog(false);
      setNewTask({ title: '', description: '', status: 'todo', assigned_to: [], start_date: new Date(), end_date: addDays(new Date(), 7), estimated_days: '', parent_task_id: '', priority_level: 2, position: 1, sprint_id: '', dependencies: [], self_assignment_enabled: false });
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleCreateReq = async () => {
    const description = stripRequirementHtml(newReq.description_html);
    if (!newReq.title.trim() || !description) return toast.error('Titre et description obligatoires');
    try {
      await requirementsApi.create({ ...newReq, description, project_id: projectId });
      toast.success('Exigence créée');
      setShowReqDialog(false);
      setNewReq({ title: '', description: '', description_html: '', type: 'functional', priority: 'medium' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateTest = async () => {
    try {
      await testsApi.create({
        ...newTest,
        project_id: projectId,
        steps: newTest.steps.filter(s => s.description.trim()).map(s => ({
          step_description: s.description,
          expected_result: s.expected_result,
        })),
      });
      toast.success('Scénario créé');
      setShowTestDialog(false);
      setNewTest({ title: '', description: '', criticality: 'major', steps: [{ description: '', expected_result: '' }] });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateMilestone = async () => {
    try {
      await milestonesApi.create({ ...newMilestone, task_id: newMilestone.task_id || null, project_id: projectId, target_date: toNoonUTC(newMilestone.target_date) });
      toast.success('Jalon créé');
      setShowMilestoneDialog(false);
      setNewMilestone({ name: '', description: '', target_date: new Date() });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // REGLE DE GESTION : Google reste un compte secondaire. Si l'utilisateur ne l'a pas
  // rattache depuis son profil, aucune creation d'evenement n'est tentee.
  const openGoogleMeeting = () => {
    if (!googleStatus.configured) { toast.error("Google Agenda n'est pas configure par l'administrateur"); return; }
    if (!googleStatus.linked) { toast.info('Rattachez votre adresse dans Mon profil > Google Agenda'); return; }
    setGoogleMeetingOpen(true);
  };

  const createGoogleProjectMeeting = async () => {
    if (!googleMeetingForm.start || !googleMeetingForm.end) { toast.error('Debut et fin obligatoires'); return; }
    try {
      const attendees = googleMeetingForm.attendees.split(/[;,\s]+/).map(v => v.trim()).filter(Boolean);
      const res = await googleCalendarApi.createProjectMeeting({
        project_id: projectId,
        title: googleMeetingForm.title || 'Réunion projet',
        description: googleMeetingForm.description || `Réunion Planigo - ${project?.name || ''}`,
        start: new Date(googleMeetingForm.start).toISOString(),
        end: new Date(googleMeetingForm.end).toISOString(),
        attendees,
      });
      setGoogleMeetingOpen(false);
      toast.success('Réunion ajoutée dans Google Agenda');
      if (res.data?.html_link) window.open(res.data.html_link, '_blank', 'noopener,noreferrer');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur Google Agenda'); }
  };

  const addTaskToGoogleCalendar = async (task) => {
    if (!googleStatus.configured) { toast.error("Google Agenda n'est pas configure par l'administrateur"); return; }
    if (!googleStatus.linked) { toast.info('Rattachez votre adresse dans Mon profil > Google Agenda'); return; }
    try {
      const res = await googleCalendarApi.createTaskEvent(task.id);
      toast.success('Tâche ajoutée dans Google Agenda');
      if (res.data?.html_link) window.open(res.data.html_link, '_blank', 'noopener,noreferrer');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur Google Agenda'); }
  };

  const uploadProjectAvatar = async (event) => {
    const file = event.target.files?.[0]; if(!file) return;
    try { await projectsApi.uploadAvatar(projectId, file); toast.success('Avatar projet mis a jour'); await fetchData(); }
    catch(e){ toast.error(e.response?.data?.detail || 'Erreur avatar projet'); }
    finally { event.target.value=''; }
  };
  const deleteProjectAvatar = async () => {
    try { await projectsApi.deleteAvatar(projectId); toast.success('Avatar projet supprime'); await fetchData(); }
    catch(e){ toast.error(e.response?.data?.detail || 'Erreur avatar projet'); }
  };

  const openEditProject = () => {
    setClosureCheck(null);
    setEditProject({
      name: project.name || '',
      code: project.code || '',
      description: project.description || '',
      status: project.status || 'draft',
      client_id: project.client_id || project.client?.id || '',
      service_id: project.service_id || project.service?.id || '',
      start_date: project.start_date ? new Date(project.start_date) : new Date(),
      end_date: project.end_date ? new Date(project.end_date) : null,
      estimated_days: project.estimated_days ?? '',
      budget: project.budget ?? '',
      accounting_order: project.accounting_order || '',
      assigned_users: project.assigned_users || [],
      notify_client: project.notify_client !== false,
    });
    setShowEditProject(true);
  };

  const analyzeProjectClosure = async () => {
    setClosureLoading(true);
    try {
      const r = await projectsApi.closureCheck(projectId);
      setClosureCheck(r.data || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur analyse cloture'); }
    finally { setClosureLoading(false); }
  };

  const handleUpdateProject = async () => {
    try {
      const data = {
        name: editProject.name,
        description: editProject.description,
        status: editProject.status,
        client_id: editProject.client_id && editProject.client_id !== 'none' ? editProject.client_id : null,
        service_id: editProject.service_id && editProject.service_id !== 'none' ? editProject.service_id : null,
        start_date: toNoonUTC(editProject.start_date) || editProject.start_date,
        end_date: toNoonUTC(editProject.end_date),
        estimated_days: editProject.estimated_days ? parseFloat(editProject.estimated_days) : null,
        budget: editProject.budget ? parseFloat(editProject.budget) : null,
        accounting_order: editProject.accounting_order || null,
        assigned_users: editProject.assigned_users,
        notify_client: editProject.notify_client !== false,
      };
      await projectsApi.update(projectId, data);
      toast.success('Projet mis a jour');
      setShowEditProject(false);
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.closure_check) {
        setClosureCheck(detail.closure_check);
        toast.error(detail.message || 'Projet non cloturable');
      } else toast.error(typeof detail === 'string' ? detail : 'Erreur mise a jour');
    }
  };

  const toggleEditUserAssignment = (userId) => {
    setEditProject(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId],
    }));
  };

  const handleUpdateReq = async () => {
    try {
      const description = stripRequirementHtml(editingReq.description_html || '');
      await requirementsApi.update(editingReq.id, {
        title: editingReq.title,
        description,
        description_html: editingReq.description_html || '',
        type: editingReq.type,
        priority: editingReq.priority,
        status: editingReq.status,
      });
      toast.success('Exigence mise a jour');
      setEditingReq(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdateMs = async () => {
    try {
      await milestonesApi.update(editingMs.id, {
        name: editingMs.name,
        description: editingMs.description,
        target_date: toNoonUTC(editingMs.target_date) || editingMs.target_date,
        status: editingMs.status,
        task_id: editingMs.task_id || null,
      });
      toast.success('Jalon mis a jour');
      setEditingMs(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await documentsApi.upload(projectId, file, '', documentCategory);
      toast.success('Document ajouté');
      fetchData();
    } catch { toast.error("Erreur upload"); }
  };

  const handleDeleteDocument = async (docId) => {
    try { await documentsApi.delete(docId); toast.success('Supprimé'); fetchData(); } catch { toast.error('Erreur'); }
  };

  const [linkDialog, setLinkDialog] = useState(false);
  const [documentCategory, setDocumentCategory] = useState('other');
  const [linkForm, setLinkForm] = useState({ name: '', url: '', description: '', category: 'other' });

  const handleAddLink = async () => {
    if (!linkForm.name || !linkForm.url) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/documents/${projectId}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...linkForm, category: linkForm.category || documentCategory }),
      });
      if (!res.ok) throw new Error();
      toast.success('Lien ajoute');
      setLinkDialog(false);
      setLinkForm({ name: '', url: '', description: '', category: 'other' });
      fetchData();
    } catch { toast.error('Erreur ajout lien'); }
  };

  // GANTT Export
  const exportGanttCSV = () => {
    const headers = ['Tache', 'Assigne(s)', 'Statut', 'Date debut', 'Date fin', 'Sous-tache de'];
    const rows = tasks.map(t => {
      const assigneeIds = Array.isArray(t.assigned_to) ? t.assigned_to : (t.assigned_to ? [t.assigned_to] : []);
      const assigneeNames = assigneeIds.map(uid => users.find(u => u.id === uid)?.name || '-').join(', ');
      const parent = tasks.find(p => p.id === t.parent_task_id);
      return [t.title, assigneeNames || '-', getLabel(t.status, 'status'), formatDate(t.start_date), formatDate(t.end_date), parent?.title || '-'];
    });
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `gantt_${project?.code || 'export'}.csv`;
    link.click();
    toast.success('Export CSV téléchargé');
  };

  const exportGanttPDF = () => {
    const printWindow = window.open('', '_blank');
    const html = `
      <html><head><title>GANTT - ${project?.name}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;} table{width:100%;border-collapse:collapse;} th,td{border:1px solid #ddd;padding:8px;text-align:left;font-size:12px;} th{background:#2563eb;color:white;} h1{font-size:18px;} h2{font-size:14px;color:#666;}</style></head>
      <body>
        <h1>Diagramme GANTT - ${project?.name} (${project?.code})</h1>
        <h2>Client: ${project?.client?.name || '-'} | Service: ${project?.service?.name || '-'}</h2>
        <table>
          <thead><tr><th>Tache</th><th>Assigne(s)</th><th>Statut</th><th>Debut</th><th>Fin</th><th>Sous-tache</th></tr></thead>
          <tbody>
            ${tasks.map(t => {
              const aIds = Array.isArray(t.assigned_to) ? t.assigned_to : (t.assigned_to ? [t.assigned_to] : []);
              const aNames = aIds.map(uid => users.find(u => u.id === uid)?.name || '-').join(', ');
              const p = tasks.find(x => x.id === t.parent_task_id);
              return `<tr><td>${t.title}</td><td>${aNames || '-'}</td><td>${getLabel(t.status, 'status')}</td><td>${formatDate(t.start_date)}</td><td>${formatDate(t.end_date)}</td><td>${p?.title || '-'}</td></tr>`;
            }).join('')}
          </tbody>
        </table>
        <p style="margin-top:20px;font-size:11px;color:#999;">Exporte le ${new Date().toLocaleDateString('fr-FR')}</p>
      </body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  // Drag & drop handlers for GANTT
  const handleGanttMouseDown = useCallback((e, task) => {
    e.preventDefault();
    e.stopPropagation();
    const ganttTimeline = e.target.closest('.gantt-timeline-proj');
    if (!ganttTimeline) return;
    const containerWidth = ganttTimeline.getBoundingClientRect().width;
    const pixelsPerDay = containerWidth / ganttData.totalDays;
    
    const taskData = { id: task.id, start_date: task.start_date, end_date: task.end_date };
    dragRef.current = { taskId: task.id, startX: e.clientX, pixelsPerDay, hasMoved: false };
    setDragInfo({ taskId: task.id, daysDelta: 0 });
    
    const onMouseMove = (me) => {
      if (!dragRef.current) return;
      const dx = me.clientX - dragRef.current.startX;
      if (Math.abs(dx) > 3) dragRef.current.hasMoved = true;
      const daysDelta = Math.round(dx / dragRef.current.pixelsPerDay);
      setDragInfo({ taskId: dragRef.current.taskId, daysDelta });
    };
    const onMouseUp = async (me) => {
      if (dragRef.current) {
        const hasMoved = dragRef.current.hasMoved;
        const dx = me.clientX - dragRef.current.startX;
        const daysDelta = Math.round(dx / dragRef.current.pixelsPerDay);
        dragRef.current = null;
        setDragInfo(null);
        if (hasMoved && daysDelta !== 0) {
          try {
            const newStart = addDays(new Date(taskData.start_date), daysDelta);
            const newEnd = addDays(new Date(taskData.end_date), daysDelta);
            await tasksApi.update(taskData.id, {
              start_date: toNoonUTC(newStart),
              end_date: toNoonUTC(newEnd),
            });
            toast.success('Dates mises a jour');
            fetchData();
          } catch {
            toast.error('Erreur de mise a jour');
          }
        } else if (!hasMoved) {
          // Click without drag -> open edit
          setEditingTask(task);
        }
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [ganttData]);

  // Helper: normalize assigned_to to array
  const getAssignees = (task) => {
    const ids = Array.isArray(task.assigned_to) ? task.assigned_to : (task.assigned_to ? [task.assigned_to] : []);
    return users.filter(u => ids.includes(u.id));
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-4">
        <div className="h-8 w-64 bg-slate-200 rounded-lg animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 text-center py-20">
        <Folders size={40} className="mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-semibold">Projet non trouvé</h3>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/projects')}>
          <ArrowLeft size={16} className="mr-2" />Retour
        </Button>
      </div>
    );
  }

  const projectChargeDays = Number(project.estimated_days ?? project.tasks_estimated_days ?? 0);
  const projectConsumedDays = Number(project.consumed_days ?? 0);
  const progress = calculateProgress(projectConsumedDays, projectChargeDays);
  const taskStatusData = stats ? TASK_STATUSES.map(s => ({
    name: s.label, value: stats.tasks.by_status[s.value] || 0, color: s.color,
  })).filter(d => d.value > 0) : [];
  const timeByUser = stats?.time?.by_user || [];
  const parentTasks = tasks.filter(t => !t.parent_task_id);

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="project-dashboard-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-to-projects-btn">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-3">
            <div className="relative group">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md overflow-hidden border border-white/60">
                {project.avatar_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${project.avatar_url}`} alt="Avatar projet" className="w-full h-full object-cover"/> : <Folders size={23} className="text-white" weight="duotone" />}
              </div>
              {hasPermission('projects:update') && <><input ref={projectAvatarInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadProjectAvatar}/><button type="button" onClick={()=>projectAvatarInputRef.current?.click()} className="absolute -right-1 -bottom-1 w-6 h-6 rounded-full bg-white border shadow flex items-center justify-center text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity" title="Changer l'avatar"><Upload size={12}/></button></>}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap"><p className="text-xs font-mono text-slate-500">{project.code}</p>{project.accounting_order&&<span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Ordre comptable : {project.accounting_order}</span>}</div>
              <h1 className="text-2xl font-heading font-extrabold text-slate-900">{project.name}</h1>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${getBadgeClass(project.status, 'status')} text-sm px-3 py-1`}>
            {getLabel(project.status, 'status')}
          </Badge>
          {project.client?.name && (
            <Badge className="bg-blue-50 text-blue-700 text-xs px-3 py-1">
              Client: {project.client.name}
            </Badge>
          )}
          {project.service?.name && (
            <Badge className="bg-violet-50 text-violet-700 text-xs px-3 py-1">
              Service: {project.service.name}
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            {googleStatus.enabled && <Button variant="outline" size="sm" className="text-xs" onClick={openGoogleMeeting} data-testid="google-project-meeting-btn" title={googleStatus.linked ? `Google Agenda : ${googleStatus.google_email || ''}` : 'Rattacher Google Agenda depuis Mon profil'}>
              <CalendarBlank size={14} className="mr-1" />Réunion Google
            </Button>}
            {hasPermission('projects:update') && <Button variant="outline" size="sm" className="text-xs" onClick={openEditProject} data-testid="edit-project-btn">
              <PencilSimple size={14} className="mr-1" />Modifier
            </Button>}
            <Button variant="outline" size="sm" className="text-xs" onClick={() => {
              window.open(projectExportApi.excelUrl(projectId), '_blank');
            }} data-testid="export-excel-btn">
              <FileXls size={14} className="mr-1 text-green-600" />Export Excel
            </Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={async () => {
              try {
                const res = await projectExportApi.pdfData(projectId);
                const data = res.data;
                const statusLabels = { todo: 'A faire', analysis: 'Analyse', development: 'Developpement', deliverable: 'Livrable', testing: 'Recette', production: 'Production', closed: 'Cloture' };
                const prioLabel = (t) => Number(t.priority_level || 0) === 0 ? 'P0 - ASAP' : `P${Number(t.priority_level || 0)}`;
                const prioColors = { 0: '#D32F2F', 1: '#F57C00', 2: '#1976D2', 3: '#455A64' };
                const statusColors = { todo: '#90CAF9', analysis: '#CE93D8', development: '#FFB74D', deliverable: '#81C784', testing: '#BA68C8', production: '#66BB6A', closed: '#A5D6A7' };
                const tasksHtml = data.tasks.map(t => `<tr style="${t.task_type === 'Sous-tache' ? 'background:#F7FAFC' : ''}">
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;font-weight:${t.task_type === 'Tache' ? '600' : '400'}">${t.task_type || 'Tache'}</td>
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${t.parent_title || ''}</td>
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;${t.task_type === 'Sous-tache' ? 'padding-left:18px;font-style:italic' : 'font-weight:600'}">${t.display_title || t.title}</td>
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${(t.assignee_names||[]).join(', ')}</td>
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center">${t.start_date ? new Date(t.start_date).toLocaleDateString('fr-FR') : ''}</td>
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center">${t.end_date ? new Date(t.end_date).toLocaleDateString('fr-FR') : ''}</td>
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;background:${statusColors[t.status]||'#eee'}20">${statusLabels[t.status]||t.status}</td>
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;color:${prioColors[Number(t.priority_level||0)]||'#333'};font-weight:600">${prioLabel(t)} / Pos.${t.position || 1}</td>
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center">${t.estimated_days != null ? Number(t.estimated_days).toFixed(2) : ''}</td>
                  <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center">${t.progress||0}%</td>
                </tr>`).join('');
                const statusRows = Object.entries(data.status_counts).map(([s,c]) => `<tr><td style="padding:4px 8px;border:1px solid #ddd;font-size:11px"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${statusColors[s]||'#ccc'};margin-right:6px"></span>${statusLabels[s]||s}</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-size:11px">${c}</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-size:11px">${data.tasks.length > 0 ? Math.round(c/data.tasks.length*100) : 0}%</td></tr>`).join('');
                const prioRows = Object.entries(data.priority_counts).map(([p,c]) => `<tr><td style="padding:4px 8px;border:1px solid #ddd;font-size:11px;color:${prioColors[Number(p)]||'#333'};font-weight:600">${Number(p) === 0 ? 'P0 - ASAP' : `P${p}`}</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-size:11px">${c}</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:center;font-size:11px">${data.tasks.length > 0 ? Math.round(c/data.tasks.length*100) : 0}%</td></tr>`).join('');
                const budgetRows = data.budget_lines.map(b => `<tr><td style="padding:4px 8px;border:1px solid #ddd;font-size:11px">${b.category}</td><td style="padding:4px 8px;border:1px solid #ddd;font-size:11px">${b.description||''}</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:right;font-size:11px">${(b.planned||0).toLocaleString('fr-FR')}</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:right;font-size:11px">${(b.consumed||0).toLocaleString('fr-FR')}</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:right;font-size:11px">${((b.planned||0)-(b.consumed||0)).toLocaleString('fr-FR')}</td></tr>`).join('');
                const html = `<html><head><title>Etat du Projet - ${data.project.name}</title><style>@media print{body{margin:0}}</style></head><body style="font-family:Calibri,Arial,sans-serif;color:#333;margin:20px">
                  <div style="text-align:center;border-bottom:3px solid #1F4E79;padding-bottom:15px;margin-bottom:20px">
                    <h1 style="color:#1F4E79;margin:0;font-size:22px">ETAT DU PROJET</h1>
                    <h2 style="color:#555;margin:4px 0 0;font-size:16px">${data.project.name} (${data.project.code})</h2>
                  </div>
                  <div style="display:flex;gap:30px;margin-bottom:20px">
                    <div><table style="border-collapse:collapse">
                      <tr><td style="padding:3px 12px 3px 0;font-weight:600;color:#555;font-size:12px">Client</td><td style="font-size:12px">${data.client_name}</td></tr>
                      <tr><td style="padding:3px 12px 3px 0;font-weight:600;color:#555;font-size:12px">Date du rapport</td><td style="font-size:12px">${new Date().toLocaleDateString('fr-FR')}</td></tr>
                      <tr><td style="padding:3px 12px 3px 0;font-weight:600;color:#555;font-size:12px">Statut</td><td style="font-size:12px">${data.project.status}</td></tr>
                    </table></div>
                    <div style="background:#E8F5E9;padding:12px 24px;border-radius:8px;text-align:center">
                      <div style="font-size:28px;font-weight:800;color:#2E7D32">${data.progress_pct}%</div>
                      <div style="font-size:11px;color:#555">Avancement</div>
                    </div>
                    <div style="background:#E3F2FD;padding:12px 24px;border-radius:8px;text-align:center">
                      <div style="font-size:16px;font-weight:700;color:#1565C0">${data.total_planned?.toLocaleString('fr-FR')||0}</div>
                      <div style="font-size:11px;color:#555">Budget planifie</div>
                      <div style="font-size:14px;font-weight:600;color:#E65100;margin-top:4px">${data.total_consumed?.toLocaleString('fr-FR')||0}</div>
                      <div style="font-size:11px;color:#555">Consomme</div>
                    </div>
                  </div>
                  <h3 style="color:#1F4E79;border-bottom:2px solid #1F4E79;padding-bottom:4px;font-size:14px">TABLEAU DES TACHES</h3>
                  <table style="width:100%;border-collapse:collapse;margin-bottom:20px"><thead><tr style="background:#1F4E79;color:white">
                    <th style="padding:6px 8px;text-align:left;font-size:10px">Type</th><th style="padding:6px 8px;text-align:left;font-size:10px">Tache parente</th>
                    <th style="padding:6px 8px;text-align:left;font-size:10px">Tache / Sous-tache</th><th style="padding:6px 8px;text-align:left;font-size:10px">Assigne(s)</th>
                    <th style="padding:6px 8px;text-align:center;font-size:10px">Debut</th><th style="padding:6px 8px;text-align:center;font-size:10px">Fin</th>
                    <th style="padding:6px 8px;text-align:left;font-size:10px">Statut</th><th style="padding:6px 8px;text-align:left;font-size:10px">Priorite / Position</th>
                    <th style="padding:6px 8px;text-align:center;font-size:10px">Charge (J)</th><th style="padding:6px 8px;text-align:center;font-size:10px">Prog.</th>
                  </tr></thead><tbody>${tasksHtml}</tbody></table>
                  <div style="display:flex;gap:20px;margin-bottom:20px">
                    <div style="flex:1"><h3 style="color:#1F4E79;border-bottom:2px solid #1F4E79;padding-bottom:4px;font-size:13px">STATUT DES TACHES (%)</h3>
                      <table style="width:100%;border-collapse:collapse"><thead><tr style="background:#1F4E79;color:white"><th style="padding:4px 8px;text-align:left;font-size:11px">Statut</th><th style="padding:4px 8px;text-align:center;font-size:11px">Nombre</th><th style="padding:4px 8px;text-align:center;font-size:11px">%</th></tr></thead><tbody>${statusRows}</tbody></table>
                    </div>
                    <div style="flex:1"><h3 style="color:#1F4E79;border-bottom:2px solid #1F4E79;padding-bottom:4px;font-size:13px">PRIORITE DES TACHES (%)</h3>
                      <table style="width:100%;border-collapse:collapse"><thead><tr style="background:#1F4E79;color:white"><th style="padding:4px 8px;text-align:left;font-size:11px">Priorite</th><th style="padding:4px 8px;text-align:center;font-size:11px">Nombre</th><th style="padding:4px 8px;text-align:center;font-size:11px">%</th></tr></thead><tbody>${prioRows}</tbody></table>
                    </div>
                  </div>
                  <h3 style="color:#1F4E79;border-bottom:2px solid #1F4E79;padding-bottom:4px;font-size:14px">BUDGET</h3>
                  <table style="width:100%;border-collapse:collapse;margin-bottom:20px"><thead><tr style="background:#1F4E79;color:white"><th style="padding:4px 8px;text-align:left;font-size:11px">Categorie</th><th style="padding:4px 8px;text-align:left;font-size:11px">Description</th><th style="padding:4px 8px;text-align:right;font-size:11px">Planifie</th><th style="padding:4px 8px;text-align:right;font-size:11px">Consomme</th><th style="padding:4px 8px;text-align:right;font-size:11px">Restant</th></tr></thead><tbody>${budgetRows}
                    <tr style="font-weight:700;background:#f5f5f5"><td colspan="2" style="padding:4px 8px;border:1px solid #ddd;font-size:11px">TOTAL</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:right;font-size:11px">${data.total_planned?.toLocaleString('fr-FR')||0}</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:right;font-size:11px">${data.total_consumed?.toLocaleString('fr-FR')||0}</td><td style="padding:4px 8px;border:1px solid #ddd;text-align:right;font-size:11px">${((data.total_planned||0)-(data.total_consumed||0)).toLocaleString('fr-FR')}</td></tr>
                  </tbody></table>
                  <div style="text-align:center;color:#999;font-size:10px;margin-top:30px;border-top:1px solid #ddd;padding-top:10px">Planigo - Rapport genere le ${new Date().toLocaleDateString('fr-FR')} a ${new Date().toLocaleTimeString('fr-FR')}</div>
                </body></html>`;
                const w = window.open('', '_blank');
                w.document.write(html);
                w.document.close();
                setTimeout(() => w.print(), 500);
              } catch (e) { toast.error('Erreur export PDF'); }
            }} data-testid="export-pdf-btn">
              <FilePdf size={14} className="mr-1 text-red-600" />Export PDF
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-gradient-blue p-4 relative overflow-hidden">
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <Target size={16} weight="duotone" />
              <span className="text-white/80 text-xs">Tâches</span>
            </div>
            <p className="text-3xl font-extrabold">{stats?.tasks?.total || 0}</p>
            <p className="text-xs text-white/80 mt-0.5">{stats?.tasks?.by_status?.closed || 0} terminées</p>
          </div>
        </Card>
        <Card className="card-gradient-orange p-4 relative overflow-hidden">
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={16} weight="duotone" />
              <span className="text-white/80 text-xs">Temps</span>
            </div>
            <p className="text-3xl font-extrabold">{(stats?.time?.consumed || 0).toFixed(1)}</p>
            <p className="text-xs text-white/80 mt-0.5">/ {stats?.time?.estimated || '?'} j estimés</p>
          </div>
        </Card>
        <Card className="card-gradient-green p-4 relative overflow-hidden">
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <ListChecks size={16} weight="duotone" />
              <span className="text-white/80 text-xs">Exigences</span>
            </div>
            <p className="text-3xl font-extrabold">{stats?.requirements?.total || 0}</p>
            <p className="text-xs text-white/80 mt-0.5">{stats?.requirements?.validation_rate || 0}% validées</p>
          </div>
        </Card>
        <Card className="card-gradient-purple p-4 relative overflow-hidden">
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <TestTube size={16} weight="duotone" />
              <span className="text-white/80 text-xs">Tests</span>
            </div>
            <p className="text-3xl font-extrabold">{stats?.tests?.total || 0}</p>
            <p className="text-xs text-white/80 mt-0.5">{stats?.tests?.success_rate || 0}% réussis</p>
          </div>
        </Card>
      </div>

      {/* Progress */}
      {projectChargeDays > 0 && (
        <Card className="card-surface p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">Charge projet</span>
            <span className={`text-sm font-bold ${getProgressColor(progress)}`}>
              {projectConsumedDays.toFixed(2)} / {projectChargeDays.toFixed(2)} J consommés ({progress}%) · {Number(project.scheduled_days||0).toFixed(2)} J positionnés
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="gantt" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="gantt">GANTT ({tasks.length})</TabsTrigger>
          <TabsTrigger value="requirements">Exigences ({requirements.length})</TabsTrigger>
          <TabsTrigger value="tests">Tests ({tests.length})</TabsTrigger>
          <TabsTrigger value="milestones">Jalons ({milestones.length})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="meetings">Réunions</TabsTrigger>
          <TabsTrigger value="time">Temps</TabsTrigger>
          <TabsTrigger value="budget" data-testid="budget-tab">Budget & Materiel</TabsTrigger>
          <TabsTrigger value="history">Historique</TabsTrigger>
        </TabsList>

        {/* GANTT Tab */}
        <TabsContent value="gantt" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
                {hasPermission('tasks:create') && <DialogTrigger asChild>
                  <Button className="btn-primary" size="sm" data-testid="create-task-btn">
                    <Plus size={14} className="mr-1" />Tâche
                  </Button>
                </DialogTrigger>}
                <DialogContent className="dialog-wide max-w-6xl h-[88vh] max-h-[88vh] overflow-hidden flex flex-col">
                  <DialogHeader><DialogTitle>Nouvelle tâche</DialogTitle></DialogHeader>
                  <div className="flex-1 min-h-0 flex flex-col mt-2">
                    <div className="grid lg:grid-cols-[1.35fr_.55fr_.45fr] gap-3 mb-4">
                      <div><Label className="text-xs">Titre *</Label><Input value={newTask.title} onChange={e=>setNewTask(p=>({...p,title:e.target.value}))} placeholder="Titre de la tâche" className="h-10 font-medium" data-testid="task-title-input"/></div>
                      <div><Label className="text-xs">Statut</Label><Select value={newTask.status} onValueChange={v=>setNewTask(p=>({...p,status:v}))}><SelectTrigger className="h-10"><SelectValue/></SelectTrigger><SelectContent>{TASK_STATUSES_EDIT.map(x=><SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label className="text-xs">Priorité</Label><Input type="number" min="0" step="1" value={newTask.priority_level} onChange={e=>setNewTask(p=>({...p,priority_level:e.target.value}))} className="h-10"/></div>
                    </div>
                    <Tabs defaultValue="general" className="flex-1 min-h-0 flex flex-col">
                      <TabsList className="w-full justify-start bg-transparent border-b rounded-none h-auto p-0"><TabsTrigger value="general" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Général</TabsTrigger><TabsTrigger value="delay" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Délais & charge</TabsTrigger><TabsTrigger value="resources" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Ressources</TabsTrigger><TabsTrigger value="deps" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Dépendances</TabsTrigger></TabsList>
                      <TabsContent value="general" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3"><div className="grid md:grid-cols-[1.3fr_.45fr] gap-3"><div className="rounded-xl border bg-white p-4"><Label>Description</Label><Textarea value={newTask.description} onChange={e=>setNewTask(p=>({...p,description:e.target.value}))} rows={5} placeholder="Description de la tâche..."/></div><div className="rounded-xl border bg-white p-4 space-y-3"><div><Label>Position</Label><Input type="number" min="1" step="1" value={newTask.position} onChange={e=>setNewTask(p=>({...p,position:e.target.value}))} className="max-w-28"/></div><div className="rounded-lg bg-blue-50 p-3 flex items-center justify-between gap-3"><div><b className="text-xs">Auto-affectation</b><p className="text-[10px] text-slate-500">L'équipe peut se positionner.</p></div><Checkbox checked={!!newTask.self_assignment_enabled} onCheckedChange={v=>setNewTask(p=>({...p,self_assignment_enabled:!!v}))}/></div></div></div></TabsContent>
                      <TabsContent value="delay" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3"><div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 text-xs text-blue-800"><b>Les dates définissent le délai.</b> La charge reste indépendante et exprimée en J/JH.</div><div className="grid lg:grid-cols-[1.2fr_.6fr] gap-3"><div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><CalendarBlank size={19} className="text-blue-600"/><b>Fenêtre de réalisation</b></div><div className="grid grid-cols-2 gap-3"><div><Label>Date début</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{format(newTask.start_date,'dd/MM/yyyy',{locale:fr})}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={newTask.start_date} onSelect={d=>d&&setNewTask(p=>({...p,start_date:d}))} locale={fr}/></PopoverContent></Popover></div><div><Label>Date fin</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{format(newTask.end_date,'dd/MM/yyyy',{locale:fr})}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={newTask.end_date} onSelect={d=>d&&setNewTask(p=>({...p,end_date:d}))} locale={fr}/></PopoverContent></Popover></div></div></div><div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><Clock size={19} className="text-indigo-600"/><b>Charge de la tâche</b></div><Label>Charge prévue (J)</Label><Input type="number" min="0" step="0.01" value={newTask.estimated_days} onChange={e=>setNewTask(p=>({...p,estimated_days:e.target.value}))} placeholder="Ex : 5,00" className="max-w-40 text-lg font-semibold"/></div></div></TabsContent>
                      <TabsContent value="resources" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3"><div className="rounded-xl border bg-white p-4"><div className="mb-3"><b>Affectation des ressources</b><p className="text-[11px] text-slate-500">Sélectionnez les ressources concernées.</p></div><MultiUserSelect users={users} selectedIds={newTask.assigned_to} onChange={v=>setNewTask(p=>({...p,assigned_to:v}))} placeholder="Sélectionner..."/></div><div className="rounded-xl border bg-slate-50 p-4 text-sm text-slate-600"><b>Répartition</b><p className="text-xs mt-1">Après création, la même fiche permet d'affecter à chaque ressource sa charge en J et ses jours de lissage.</p></div></TabsContent>
                      <TabsContent value="deps" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3"><div className="grid md:grid-cols-2 gap-3"><div className="rounded-xl border bg-white p-4"><Label>Sous-tâche de</Label><Select value={newTask.parent_task_id||'none'} onValueChange={v=>{const parentId=v==='none'?'':v;const parent=parentTasks.find(t=>t.id===parentId);if(!parent){setNewTask(p=>({...p,parent_task_id:''}));return;}const siblings=tasks.filter(t=>t.parent_task_id===parentId&&Number(t.priority_level??2)===Number(newTask.priority_level??2));const nextPosition=siblings.length?Math.max(...siblings.map(t=>Number(t.position||0)))+1:1;setNewTask(p=>({...p,parent_task_id:parentId,start_date:parent.start_date?new Date(parent.start_date):p.start_date,end_date:parent.end_date?new Date(parent.end_date):p.end_date,assigned_to:p.assigned_to?.length?p.assigned_to:(parent.assigned_to||[]),position:nextPosition}));}}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="none">Aucune</SelectItem>{parentTasks.map(t=><SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent></Select></div><div className="rounded-xl border bg-white p-4"><Label>Sprint</Label><Select value={newTask.sprint_id||'none'} onValueChange={v=>setNewTask(p=>({...p,sprint_id:v==='none'?'':v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="none">Aucun sprint</SelectItem>{sprints.map(sp=><SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}</SelectContent></Select></div></div><div className="rounded-xl border bg-white p-4"><Label>Dépend de</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start font-normal">{newTask.dependencies?.length?`${newTask.dependencies.length} dépendance(s)`:'Aucune dépendance'}</Button></PopoverTrigger><PopoverContent className="w-80 p-2"><div className="max-h-52 overflow-y-auto">{tasks.filter(t=>t.id!==newTask.parent_task_id).map(t=><label key={t.id} className="flex gap-2 items-center p-1.5 rounded hover:bg-slate-50 text-xs"><Checkbox checked={(newTask.dependencies||[]).includes(t.id)} onCheckedChange={ok=>setNewTask(p=>({...p,dependencies:ok?[...(p.dependencies||[]),t.id]:(p.dependencies||[]).filter(x=>x!==t.id)}))}/><span>{t.parent_task_id?'↳ ':''}{t.title}</span></label>)}</div></PopoverContent></Popover></div></TabsContent>
                    </Tabs>
                    <div className="mt-4 -mx-6 px-6 py-3 border-t bg-white flex justify-end gap-2"><Button variant="outline" onClick={()=>setShowTaskDialog(false)}>Annuler</Button><Button className="btn-primary" onClick={handleCreateTask} disabled={!newTask.title} data-testid="submit-task-btn">Créer</Button></div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={exportGanttCSV} data-testid="export-csv-btn">
                <FileXls size={14} className="mr-1" />Excel/CSV
              </Button>
              <Button variant="outline" size="sm" onClick={exportGanttPDF} data-testid="export-pdf-btn">
                <FilePdf size={14} className="mr-1" />PDF
              </Button>
            </div>
          </div>

          {/* GANTT Chart */}
          <Card className="card-surface overflow-hidden">
            <CardHeader className="pb-2">
              <span className="text-xs text-slate-400">Glissez les barres pour modifier les dates</span>
            </CardHeader>
            <CardContent className="p-0">
              {tasks.length === 0 ? (
                <div className="py-12 text-center">
                  <Target size={36} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Aucune tache. Creez votre premiere tache.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="flex min-w-[900px]">
                    {/* Left panel: task info */}
                    <div className="w-[420px] flex-shrink-0 border-r border-slate-200">
                      <div className="grid grid-cols-[1fr_100px_80px] bg-slate-100 border-b text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                        <div className="px-3 py-2">Tache</div>
                        <div className="px-2 py-2">Assigne(s)</div>
                        <div className="px-2 py-2">Statut</div>
                      </div>
                      {hierarchicalTasks.map(task => {
                        const assignees = getAssignees(task);
                        const isSubtask = !!task.parent_task_id;
                        return (
                          <div key={task.id} className="grid grid-cols-[1fr_100px_80px] border-b border-slate-100 items-center h-9 cursor-pointer hover:bg-slate-50" data-testid={`gantt-task-${task.id}`} onClick={() => setEditingTask(task)}>
                            <div className={`px-3 truncate text-xs font-medium text-slate-800 ${isSubtask ? 'pl-7' : ''}`}>
                              {isSubtask && <span className="text-slate-300 mr-1">&#8627;</span>}
                              <span className="text-[9px] text-blue-600 mr-1">P{task.priority_level ?? 2}.{task.position ?? 1}</span>
                              {task.title}
                              <span className="text-[9px] text-slate-400 ml-1">{task.progress ?? 0}%</span>
                              {task.sprint_id && <span className="ml-1 text-[8px] rounded bg-indigo-50 text-indigo-700 px-1">{sprints.find(sp=>sp.id===task.sprint_id)?.name || 'Sprint'}</span>}
                            </div>
                            <div className="px-2 truncate">
                              {assignees.length > 0 ? (
                                <div className="flex items-center gap-0.5">
                                  <div className="flex -space-x-1.5">
                                    {assignees.slice(0, 2).map(u => (
                                      <Avatar key={u.id} className="w-5 h-5 border border-white"><AvatarFallback className="text-[7px] bg-blue-100 text-blue-700">{getInitials(u.name)}</AvatarFallback></Avatar>
                                    ))}
                                  </div>
                                  {assignees.length > 2 && <span className="text-[9px] text-slate-500">+{assignees.length - 2}</span>}
                                  {assignees.length <= 2 && <span className="text-[10px] text-slate-600 truncate ml-1">{assignees[0].name.split(' ')[0]}</span>}
                                </div>
                              ) : <span className="text-[10px] text-slate-400">-</span>}
                            </div>
                            <div className="px-2">
                              <Badge className={`${getBadgeClass(task.status, 'status')} text-[8px] px-1 py-0`}>
                                {getLabel(task.status, 'status')}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                      {sprints.length > 0 && (
                        <div className="grid grid-cols-[1fr_100px_80px] border-t border-indigo-200 items-center h-8 bg-indigo-50/40">
                          <div className="px-3 text-xs font-semibold text-indigo-700">Sprints ({sprints.length})</div><div></div><div></div>
                        </div>
                      )}
                      {/* Milestones row in left panel */}
                      {milestones.length > 0 && (
                        <div className="grid grid-cols-[1fr_100px_80px] border-t-2 border-dashed border-orange-200 items-center h-8 bg-orange-50/30">
                          <div className="px-3 text-xs font-semibold text-orange-700 flex items-center gap-1">
                            <Flag size={12} />{milestones.length} jalon{milestones.length > 1 ? 's' : ''}
                          </div>
                          <div></div>
                          <div></div>
                        </div>
                      )}
                    </div>
                    {/* Right panel: timeline with date headers */}
                    <div className="flex-1 min-w-[480px] gantt-timeline-proj">
                      {/* Date scale header */}
                      <div className="relative bg-slate-100 border-b h-[29px] flex">
                        {ganttData.dateCols.map((d, i) => {
                          const leftPct = ((d - ganttData.minDate) / 86400000 / ganttData.totalDays) * 100;
                          return (
                            <div key={i} className="absolute top-0 h-full flex flex-col justify-center border-l border-slate-200" style={{ left: `${leftPct}%` }}>
                              <span className="text-[9px] text-slate-500 font-medium px-1 whitespace-nowrap">
                                {format(d, 'dd/MM', { locale: fr })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {/* Task bars */}
                      {hierarchicalTasks.map(task => {
                        const assignees = getAssignees(task);
                        const start = new Date(task.start_date);
                        const end = new Date(task.end_date);
                        const offsetPct = ((start - ganttData.minDate) / 86400000 / ganttData.totalDays) * 100;
                        const widthPct = (Math.max((end - start) / 86400000, 1) / ganttData.totalDays) * 100;
                        const isDragging = dragInfo?.taskId === task.id;
                        const dragOffsetPct = isDragging ? ((dragInfo.daysDelta / ganttData.totalDays) * 100) : 0;
                        return (
                          <div key={task.id} className="relative h-9 border-b border-slate-50">
                            {ganttData.dateCols.map((d, i) => {
                              const lPct = ((d - ganttData.minDate) / 86400000 / ganttData.totalDays) * 100;
                              return <div key={i} className="absolute top-0 bottom-0 border-l border-slate-100" style={{ left: `${lPct}%` }} />;
                            })}
                            <div
                              className={`absolute top-1.5 h-6 rounded shadow-sm flex items-center px-1.5 ${isDragging ? 'opacity-70 ring-2 ring-blue-400' : 'hover:ring-2 hover:ring-blue-300'}`}
                              style={{
                                left: `${Math.max(offsetPct + dragOffsetPct, 0)}%`,
                                width: `${Math.max(widthPct, 1.5)}%`,
                                backgroundColor: getTaskStatusColor(task.status),
                                opacity: task.parent_task_id ? 0.82 : 1,
                                borderLeft: task.parent_task_id ? '4px solid rgba(255,255,255,.75)' : undefined,
                                minWidth: '10px',
                                cursor: 'grab',
                              }}
                              onMouseDown={(e) => handleGanttMouseDown(e, task)}
                              onClick={(e) => {
                                if (!dragInfo) {
                                  e.stopPropagation();
                                  setEditingTask(task);
                                }
                              }}
                              title={`${task.title} | ${formatDate(task.start_date)} - ${formatDate(task.end_date)}${assignees.length > 0 ? ` | ${assignees.map(u=>u.name).join(', ')}` : ''} | Cliquez pour modifier, glissez pour deplacer`}
                            >
                              <ArrowsHorizontal size={9} className="mr-0.5 text-white/60 flex-shrink-0" />
                              <span className="text-[9px] text-white font-medium truncate">
                                {widthPct > 6 ? `${task.parent_task_id ? '↳ ' : ''}${task.title} · ${task.progress ?? 0}%` : ''}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {sprints.length > 0 && (
                        <div className="relative h-8 border-t border-indigo-200 bg-indigo-50/30">
                          {sprints.map(sp => {
                            const st=new Date(sp.start_date), en=new Date(sp.end_date);
                            const left=((st-ganttData.minDate)/86400000/ganttData.totalDays)*100;
                            const width=(Math.max((en-st)/86400000,1)/ganttData.totalDays)*100;
                            return <div key={sp.id} className="absolute top-1.5 h-5 rounded bg-indigo-500/80 text-white text-[9px] font-semibold px-2 truncate" style={{left:`${Math.max(left,0)}%`,width:`${Math.max(width,2)}%`}} title={`${sp.name} - ${formatDate(sp.start_date)} au ${formatDate(sp.end_date)}`}>{sp.name}</div>;
                          })}
                        </div>
                      )}
                      {/* Milestone diamonds */}
                      {milestones.length > 0 && (
                        <div className="relative h-8 border-t-2 border-dashed border-orange-200 bg-orange-50/30">
                          {ganttData.dateCols.map((d, i) => {
                            const lPct = ((d - ganttData.minDate) / 86400000 / ganttData.totalDays) * 100;
                            return <div key={i} className="absolute top-0 bottom-0 border-l border-slate-100" style={{ left: `${lPct}%` }} />;
                          })}
                          {milestones.map(ms => {
                            const msDate = new Date(ms.target_date);
                            const msPct = ((msDate - ganttData.minDate) / 86400000 / ganttData.totalDays) * 100;
                            const isAchieved = ms.status === 'achieved';
                            const isOverdue = msDate < new Date() && !isAchieved;
                            return (
                              <div key={ms.id} className="absolute top-1" style={{ left: `${msPct}%`, transform: 'translateX(-50%)' }}
                                title={`Jalon: ${ms.name} - ${formatDate(ms.target_date)}${isAchieved ? ' (Atteint)' : isOverdue ? ' (En retard)' : ''}`}
                                data-testid={`gantt-milestone-${ms.id}`}>
                                <div className={`w-5 h-5 rotate-45 border-2 ${isAchieved ? 'bg-green-500 border-green-600' : isOverdue ? 'bg-red-500 border-red-600' : 'bg-orange-400 border-orange-500'} shadow-sm`} />
                                <span className={`absolute top-6 left-1/2 -translate-x-1/2 text-[8px] font-semibold whitespace-nowrap ${isAchieved ? 'text-green-700' : isOverdue ? 'text-red-700' : 'text-orange-700'}`}>
                                  {ms.name.length > 15 ? ms.name.slice(0, 15) + '...' : ms.name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Task status distribution */}
          {taskStatusData.length > 0 && (
            <Card className="card-surface">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  {taskStatusData.map(d => (
                    <div key={d.name} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-xs text-slate-600">{d.name}: <strong>{d.value}</strong></span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Requirements Tab */}
        <TabsContent value="requirements" className="space-y-4">
          <div className="flex justify-end gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigate('/requirements')}><FileText size={14} className="mr-1" />Pièces jointes / liens</Button>
            <Dialog open={showReqDialog} onOpenChange={setShowReqDialog}>
              {hasPermission('requirements:create') && <DialogTrigger asChild>
                <Button className="btn-primary" size="sm" data-testid="create-req-btn">
                  <Plus size={14} className="mr-1" />Exigence
                </Button>
              </DialogTrigger>}
              <DialogContent>
                <DialogHeader><DialogTitle>Nouvelle exigence</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-3">
                  <div className="space-y-1.5">
                    <Label>Titre *</Label>
                    <Input value={newReq.title} onChange={e => setNewReq(p => ({ ...p, title: e.target.value }))} data-testid="req-title-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Description mise en page</Label>
                    <ProjectRequirementEditor value={newReq.description_html} onChange={v => setNewReq(p => ({ ...p, description_html: v }))} />
                    <p className="text-[10px] text-slate-500">Le code est généré automatiquement. Les photos, PDF, Word, Excel et liens Web sont disponibles dans l'écran Exigences.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Type</Label>
                      <Select value={newReq.type} onValueChange={v => setNewReq(p => ({ ...p, type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="business">Métier</SelectItem>
                          <SelectItem value="functional">Fonctionnelle</SelectItem>
                          <SelectItem value="technical">Technique</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Priorité</Label>
                      <Select value={newReq.priority} onValueChange={v => setNewReq(p => ({ ...p, priority: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Basse</SelectItem>
                          <SelectItem value="medium">Moyenne</SelectItem>
                          <SelectItem value="high">Haute</SelectItem>
                          <SelectItem value="critical">Critique</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setShowReqDialog(false)}>Annuler</Button>
                    <Button className="btn-primary" onClick={handleCreateReq} disabled={!newReq.title} data-testid="submit-req-btn">Créer</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="card-surface">
            <CardContent className="p-0">
              {requirements.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucune exigence</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {requirements.map(req => (
                    <div key={req.id} className="flex items-center justify-between p-3 hover:bg-slate-50" data-testid={`project-req-${req.id}`}>
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="font-mono text-[10px] text-blue-600 font-bold">{req.code || '—'}</p>
                        <p className="font-medium text-sm">{req.title}</p>
                        <p className="text-xs text-slate-500 line-clamp-1">{req.description}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className={getBadgeClass(req.priority, 'priority')}>{getLabel(req.priority, 'priority')}</Badge>
                        <Badge className={getBadgeClass(req.type, 'type')}>{getLabel(req.type, 'type')}</Badge>
                        <Badge className={getBadgeClass(req.status, 'status')}>{getLabel(req.status, 'status')}</Badge>
                        {hasPermission('requirements:update') && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingReq({ ...req, description_html: req.description_html || `<p>${req.description || ''}</p>` })} data-testid={`edit-req-${req.id}`}>
                          <PencilSimple size={14} className="text-slate-500" />
                        </Button>}
                        {hasPermission('requirements:delete') && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={async () => {
                          try { await requirementsApi.delete(req.id); toast.success('Exigence supprimee'); fetchData(); } catch { toast.error('Erreur'); }
                        }} data-testid={`delete-req-${req.id}`}>
                          <Trash size={14} />
                        </Button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tests Tab */}
        <TabsContent value="tests" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
              {hasPermission('tests:create') && <DialogTrigger asChild>
                <Button className="btn-primary" size="sm" data-testid="create-test-from-project-btn">
                  <Plus size={14} className="mr-1" />Scénario de test
                </Button>
              </DialogTrigger>}
              <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Nouveau scénario de test</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-3">
                  <div className="space-y-1.5">
                    <Label>Titre *</Label>
                    <Input value={newTest.title} onChange={e => setNewTest(p => ({ ...p, title: e.target.value }))} data-testid="test-title-input-proj" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Description</Label>
                    <Textarea value={newTest.description} onChange={e => setNewTest(p => ({ ...p, description: e.target.value }))} rows={2} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">Étapes</Label>
                      <Button variant="outline" size="sm" onClick={() => setNewTest(p => ({ ...p, steps: [...p.steps, { description: '', expected_result: '' }] }))}>
                        <Plus size={12} className="mr-1" />Étape
                      </Button>
                    </div>
                    {newTest.steps.map((s, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-600">Étape {i + 1}</span>
                          {newTest.steps.length > 1 && (
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => setNewTest(p => ({ ...p, steps: p.steps.filter((_, j) => j !== i) }))}>
                              <Trash size={12} />
                            </Button>
                          )}
                        </div>
                        <Input placeholder="Action..." value={s.description} onChange={e => {
                          const steps = [...newTest.steps]; steps[i] = { ...steps[i], description: e.target.value }; setNewTest(p => ({ ...p, steps }));
                        }} />
                        <Input placeholder="Résultat attendu..." value={s.expected_result} onChange={e => {
                          const steps = [...newTest.steps]; steps[i] = { ...steps[i], expected_result: e.target.value }; setNewTest(p => ({ ...p, steps }));
                        }} />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setShowTestDialog(false)}>Annuler</Button>
                    <Button className="btn-primary" onClick={handleCreateTest} disabled={!newTest.title}>Créer</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="card-surface">
            <CardContent className="p-0">
              {tests.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucun scénario</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {tests.map(test => (
                    <div key={test.id} className="flex items-center justify-between p-3 hover:bg-slate-50" data-testid={`project-test-${test.id}`}>
                      <div>
                        <p className="font-medium text-sm">{test.title}</p>
                        <p className="text-xs text-slate-500">{test.steps?.length || 0} étape(s) - v{test.version}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getBadgeClass(test.criticality, 'criticality')}>{getLabel(test.criticality, 'criticality')}</Badge>
                        <Badge className={getBadgeClass(test.status || 'not_executed', 'status')}>{getLabel(test.status || 'not_executed', 'status')}</Badge>
                        <Button variant="outline" size="sm" onClick={()=>navigate(`/tests?project=${projectId}`)}>Executer</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Milestones Tab */}
        <TabsContent value="milestones" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showMilestoneDialog} onOpenChange={setShowMilestoneDialog}>
              {hasPermission('milestones:create') && <DialogTrigger asChild>
                <Button className="btn-primary" size="sm" data-testid="create-milestone-from-project-btn">
                  <Plus size={14} className="mr-1" />Jalon
                </Button>
              </DialogTrigger>}
              <DialogContent>
                <DialogHeader><DialogTitle>Nouveau jalon</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-3">
                  <div className="space-y-1.5">
                    <Label>Nom *</Label>
                    <Input value={newMilestone.name} onChange={e => setNewMilestone(p => ({ ...p, name: e.target.value }))} data-testid="milestone-name-proj" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Description</Label>
                    <Textarea value={newMilestone.description} onChange={e => setNewMilestone(p => ({ ...p, description: e.target.value }))} rows={2} />
                  </div>
                  <div className="space-y-1.5"><Label>Rattachement (optionnel)</Label><Select value={newMilestone.task_id || 'project'} onValueChange={v=>setNewMilestone(p=>({...p,task_id:v==='project'?'':v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="project">Projet global</SelectItem>{tasks.map(t=><SelectItem key={t.id} value={t.id}>{t.parent_task_id?'↳ ':''}{t.title}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-1.5">
                    <Label>Date cible</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-sm">
                          <CalendarBlank size={14} className="mr-2" />{format(newMilestone.target_date, 'dd/MM/yyyy', { locale: fr })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={newMilestone.target_date} onSelect={d => d && setNewMilestone(p => ({ ...p, target_date: d }))} locale={fr} /></PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setShowMilestoneDialog(false)}>Annuler</Button>
                    <Button className="btn-primary" onClick={handleCreateMilestone} disabled={!newMilestone.name}>Créer</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="card-surface">
            <CardContent className="p-0">
              {milestones.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucun jalon</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {milestones.map(ms => {
                    const isOverdue = new Date(ms.target_date) < new Date() && ms.status !== 'achieved';
                    return (
                      <div key={ms.id} className={`flex items-center justify-between p-3 ${isOverdue ? 'bg-red-50' : 'hover:bg-slate-50'}`} data-testid={`project-milestone-${ms.id}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0 mr-3">
                          {ms.status === 'achieved' ? <CheckCircle size={16} className="text-green-600 flex-shrink-0" /> : isOverdue ? <Warning size={16} className="text-red-600 flex-shrink-0" /> : <Flag size={16} className="text-amber-500 flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="font-medium text-sm">{ms.name}</p>
                            <p className="text-xs text-slate-500">{ms.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-slate-600'}`}>{formatDate(ms.target_date)}</span>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingMs(ms)} data-testid={`edit-ms-${ms.id}`}>
                            <PencilSimple size={14} className="text-slate-500" />
                          </Button>
                          {ms.status !== 'achieved' && (
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={async () => {
                              try { await milestonesApi.update(ms.id, { status: 'achieved' }); toast.success('Jalon atteint'); fetchData(); } catch { toast.error('Erreur'); }
                            }}>Atteint</Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={async () => {
                            try { await milestonesApi.delete(ms.id); toast.success('Jalon supprime'); fetchData(); } catch { toast.error('Erreur'); }
                          }} data-testid={`delete-ms-${ms.id}`}>
                            <Trash size={14} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <Card className="card-surface">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3"><Label className="text-xs whitespace-nowrap">Catégorie du document</Label><Select value={documentCategory} onValueChange={setDocumentCategory}><SelectTrigger className="w-56"><SelectValue/></SelectTrigger><SelectContent>{(documentCategories?.length?documentCategories:[{code:'budget',label:'Budget'},{code:'other',label:'Autre'}]).map(x=><SelectItem key={x.code} value={x.code}>{x.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="flex gap-3">
                <label htmlFor="proj-doc-upload" className="cursor-pointer flex-1">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                    <Upload size={24} className="mx-auto text-slate-400 mb-1" />
                    <p className="text-sm font-medium text-slate-700">Importer un fichier</p>
                    <p className="text-xs text-slate-500">PDF, Word, Excel, Images</p>
                  </div>
                </label>
                <div className="flex-1 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-green-400 transition-colors cursor-pointer" onClick={() => setLinkDialog(true)}>
                  <LinkSimple size={24} className="mx-auto text-slate-400 mb-1" />
                  <p className="text-sm font-medium text-slate-700">Ajouter un lien web</p>
                  <p className="text-xs text-slate-500">Drive, SharePoint, URL</p>
                </div>
              </div>
              <input id="proj-doc-upload" type="file" className="hidden" onChange={handleUploadDocument} data-testid="doc-upload-input" />
              {documents.length === 0 ? (
                <p className="text-center text-slate-500 py-4">Aucun document</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Fichier</TableHead>
                      <TableHead className="font-semibold">Taille</TableHead>
                      <TableHead className="font-semibold">Par</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Client</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map(doc => {
                      const isLink = doc.is_link || doc.content_type === 'link';
                      const href = isLink ? doc.file_url : `${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`;
                      return (
                      <TableRow key={doc.id} data-testid={`project-doc-${doc.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {isLink ? <GlobeSimple size={16} className="text-green-600 flex-shrink-0" /> : <FileText size={16} className="text-blue-600 flex-shrink-0" />}
                            <div>
                              <a href={href} target="_blank" rel="noopener noreferrer" className={`text-sm ${isLink ? 'text-green-600' : 'text-blue-600'} hover:underline font-medium truncate max-w-[200px] block`}>
                                {doc.original_name}
                              </a>
                              <div className="flex gap-1 mt-0.5">{doc.category&&<Badge variant="outline" className="text-[9px]">{doc.category}</Badge>}{isLink&&doc.provider&&<Badge className="bg-slate-100 text-slate-600 text-[9px]">{doc.provider==='google_drive'?'Google Drive':doc.provider==='sharepoint'?'Microsoft SharePoint':doc.provider==='onedrive'?'Microsoft OneDrive':doc.provider==='teams'?'Microsoft Teams':doc.provider==='dropbox'?'Dropbox':'Lien web'}</Badge>}</div>{doc.description && <p className="text-[10px] text-slate-400">{doc.description}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">{isLink ? <Badge className="bg-green-50 text-green-600 text-[10px]">Lien</Badge> : doc.size ? `${(doc.size / 1024).toFixed(0)} Ko` : '-'}</TableCell>
                        <TableCell className="text-xs text-slate-500">{doc.uploaded_by_name || '-'}</TableCell>
                        <TableCell className="text-xs text-slate-500">{doc.created_at ? formatDate(doc.created_at) : '-'}</TableCell>
                        <TableCell><Button variant="ghost" size="sm" className={doc.client_visible ? "text-emerald-700 bg-emerald-50" : "text-slate-500"} onClick={async()=>{ try { await documentsApi.toggleClientVisible(doc.id,!doc.client_visible); toast.success(!doc.client_visible?"Document partage avec le client":"Partage client retire"); fetchData(); } catch { toast.error("Erreur partage client"); } }}>{doc.client_visible ? "Partage" : "Interne"}</Button></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <a href={href} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600">{isLink ? <GlobeSimple size={14} /> : <Download size={14} />}</Button>
                            </a>
                            <Button variant="ghost" size="icon" className="text-red-500 h-7 w-7" onClick={() => handleDeleteDocument(doc.id)}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Link Dialog */}
          <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Ajouter un lien web</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-3">
                <div className="space-y-1">
                  <Label className="text-xs">Nom *</Label>
                  <Input value={linkForm.name} onChange={e => setLinkForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Documents projet sur Drive" data-testid="link-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">URL *</Label>
                  <Input value={linkForm.url} onChange={e => setLinkForm(p => ({ ...p, url: e.target.value }))} placeholder="https://drive.google.com/..." data-testid="link-url-input" />
                </div>
                <div className="space-y-1"><Label className="text-xs">Catégorie</Label><Select value={linkForm.category||documentCategory} onValueChange={v=>setLinkForm(p=>({...p,category:v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{(documentCategories?.length?documentCategories:[{code:'budget',label:'Budget'},{code:'other',label:'Autre'}]).map(x=><SelectItem key={x.code} value={x.code}>{x.label}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input value={linkForm.description} onChange={e => setLinkForm(p => ({ ...p, description: e.target.value }))} placeholder="Description optionnelle" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setLinkDialog(false)}>Annuler</Button>
                  <Button className="btn-primary" onClick={handleAddLink} disabled={!linkForm.name || !linkForm.url} data-testid="submit-link-btn">Ajouter</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Meetings Tab - V5 */}
        <TabsContent value="meetings" className="space-y-4">
          <ProjectMeetingsPanel project={project} users={users} />
        </TabsContent>

        {/* Time Tab */}
        <TabsContent value="time" className="space-y-4">
          {/* Time KPI Cards */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="card-surface">
              <CardContent className="pt-4">
                <p className="text-xs text-slate-500 uppercase font-semibold">Charge prévue</p>
                <p className="text-2xl font-bold text-slate-900">{projectChargeDays.toFixed(2)} J</p>
              </CardContent>
            </Card>
            <Card className="card-surface border-l-4 border-l-blue-500">
              <CardContent className="pt-4">
                <p className="text-xs text-slate-500 uppercase font-semibold">Consommé</p>
                <p className="text-2xl font-bold text-blue-600">{projectConsumedDays.toFixed(2)} J</p>
                {projectChargeDays > 0 && (
                  <Progress value={Math.min((projectConsumedDays / projectChargeDays) * 100, 100)} className="h-1.5 mt-2" />
                )}
              </CardContent>
            </Card>
            <Card className="card-surface">
              <CardContent className="pt-4">
                <p className="text-xs text-slate-500 uppercase font-semibold">Reste charge</p>
                <p className={`text-2xl font-bold ${(projectChargeDays-projectConsumedDays) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(projectChargeDays-projectConsumedDays).toFixed(2)} J
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick time entry + chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Quick entry form */}
            <Card className="card-surface">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-heading">Saisie rapide</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Tache / sous-tache *</Label>
                  <Select value={newTimeEntry.task_id} onValueChange={v => setNewTimeEntry(p => ({ ...p, task_id: v }))}>
                    <SelectTrigger className="h-8 text-sm" data-testid="project-time-task-select"><SelectValue placeholder="Associer a une tache" /></SelectTrigger>
                    <SelectContent>
                      {tasks.sort((a,b)=>Number(a.priority_level||0)-Number(b.priority_level||0)||Number(a.position||0)-Number(b.position||0)).map(t => { const parent=tasks.find(x=>x.id===t.parent_task_id); return <SelectItem key={t.id} value={t.id}>{parent ? `↳ ${parent.title} > ${t.title}` : `▰ ${t.title}`}</SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Activite</Label>
                    <Select value={newTimeEntry.activity} onValueChange={v => setNewTimeEntry(p => ({ ...p, activity: v }))}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['Developpement','Tests','Analyse','Conception','Reunion','Documentation','Support'].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Temps (jour)</Label>
                    <Input type="number" step="0.01" min="0.01" max="1" className="h-8 text-sm" value={newTimeEntry.time_spent}
                      onChange={e => setNewTimeEntry(p => ({ ...p, time_spent: e.target.value }))} placeholder="0.25" data-testid="project-time-spent-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 text-sm justify-start">
                          <CalendarBlank size={12} className="mr-1" />
                          {format(newTimeEntry.date, 'dd/MM', { locale: fr })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={newTimeEntry.date} onSelect={d => d && setNewTimeEntry(p => ({ ...p, date: d }))} locale={fr} />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Description</Label>
                  <Input className="h-8 text-sm" value={newTimeEntry.description} onChange={e => setNewTimeEntry(p => ({ ...p, description: e.target.value }))} placeholder="Details..." />
                </div>
                <label className="flex items-center gap-2 text-xs font-medium cursor-pointer"><input type="checkbox" checked={newTimeEntry.billable !== false} onChange={e=>setNewTimeEntry(p=>({...p,billable:e.target.checked}))}/> Facturable</label>
                <Button className="btn-primary w-full" data-testid="project-time-submit-btn" disabled={!newTimeEntry.task_id || !newTimeEntry.time_spent || !newTimeEntry.activity}
                  onClick={async () => {
                    try {
                      await timeApi.create({
                        project_id: projectId,
                        task_id: newTimeEntry.task_id,
                        activity: newTimeEntry.activity,
                        date: toNoonUTC(newTimeEntry.date),
                        time_spent: parseFloat(newTimeEntry.time_spent),
                        description: newTimeEntry.description,
                        billable: newTimeEntry.billable !== false,
                      });
                      toast.success('Temps enregistre');
                      setNewTimeEntry({ task_id: '', activity: 'Developpement', date: new Date(), time_spent: '', description: '', billable: true });
                      fetchData();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                  }}>
                  <Plus size={14} className="mr-1" />Enregistrer
                </Button>
              </CardContent>
            </Card>

            {/* Time by user chart */}
            <Card className="card-surface">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Temps par utilisateur</CardTitle>
              </CardHeader>
              <CardContent>
                {timeByUser.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucun temps saisi</p>
                ) : (
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={timeByUser}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="user_name" tick={{ fontSize: 11, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `${v}j`} />
                        <Tooltip formatter={v => [`${Number(v).toFixed(2)} j`, 'Temps']} />
                        <Bar dataKey="total" fill="#2563eb" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Budget & Material Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-base">Historique du projet</CardTitle><p className="text-xs text-slate-500">Traçabilité métier et technique, avec identifiants pseudonymisés.</p></CardHeader><CardContent className="p-0"><div className="divide-y">{history.length?history.map(h=><div key={h.id} className="px-4 py-3 flex gap-3"><div className={`w-2 h-2 mt-1.5 rounded-full ${h.success?'bg-emerald-500':'bg-red-500'}`}/><div className="min-w-0 flex-1"><div className="flex items-center justify-between gap-3"><b className="text-xs">{h.action}</b><span className="text-[10px] text-slate-400">{h.created_at?new Date(h.created_at).toLocaleString('fr-FR'):'-'}</span></div><div className="text-[10px] text-slate-500 mt-1">{h.module} · {h.object_type||'objet'} {h.object_id?`#${String(h.object_id).slice(-6)}`:''} · Utilisateur #{h.user_ref||'-'}</div>{h.changes&&Object.keys(h.changes).length>0&&<div className="mt-2 rounded-lg bg-slate-50 p-2 text-[10px] text-slate-600">{Object.entries(h.changes).map(([k,v])=><div key={k}><b>{k}</b> : {String(v)}</div>)}</div>}</div></div>):<div className="p-8 text-center text-sm text-slate-400">Aucun événement enregistré.</div>}</div></CardContent></Card>
        </TabsContent>

        <TabsContent value="budget" className="space-y-4">
          {/* Export Button */}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/assets?project=${projectId}`)}>
              Parc matériel lié au projet
            </Button>
            <label className="inline-flex"><input type="file" accept=".xlsx" className="hidden" onChange={async e=>{ const file=e.target.files?.[0]; if(!file)return; setBudgetImporting(true); try { const r=await budgetsApi.importPurchases(projectId,file); toast.success(`Import achats : ${r.data.created} créée(s), ${r.data.updated} mise(s) à jour, ${r.data.ignored} ignorée(s)`); fetchData(); } catch(err){ toast.error(err.response?.data?.detail || 'Erreur import achats'); } finally { setBudgetImporting(false); e.target.value=''; } }} /><Button variant="outline" size="sm" disabled={budgetImporting}><Upload size={14} className="mr-1"/>Importer commandes</Button></label>
            <a href={budgetsApi.exportCSV(projectId)} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="export-budget-btn">
                <Download size={14} className="mr-1" />Exporter CSV
              </Button>
            </a>
          </div>
          {/* Budget Summary KPIs */}
          {budgetSummary && (
            <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
              <Card className="card-surface"><CardContent className="pt-4"><p className="text-xs text-slate-500 uppercase font-semibold">Budget initial</p><p className="text-2xl font-bold text-slate-900">{Number(budgetSummary.budget_initial||0).toFixed(0)} &euro;</p></CardContent></Card>
              <Card className="card-surface"><CardContent className="pt-4"><p className="text-xs text-slate-500 uppercase font-semibold">Ajustements</p><p className="text-2xl font-bold text-violet-700">{Number(budgetSummary.budget_adjustments||0).toFixed(0)} &euro;</p></CardContent></Card>
              <Card className="card-surface"><CardContent className="pt-4"><p className="text-xs text-slate-500 uppercase font-semibold">Budget en cours</p><p className="text-2xl font-bold text-slate-900">{Number(budgetSummary.budget_current||0).toFixed(0)} &euro;</p></CardContent></Card>
              <Card className="card-surface"><CardContent className="pt-4"><p className="text-xs text-slate-500 uppercase font-semibold">Engagé</p><p className="text-2xl font-bold text-blue-700">{budgetSummary.total_budget_engaged?.toFixed(0) || 0} &euro;</p></CardContent></Card>
              <Card className="card-surface"><CardContent className="pt-4"><p className="text-xs text-slate-500 uppercase font-semibold">Réalisé</p><p className="text-2xl font-bold text-orange-600">{budgetSummary.total_budget_consumed?.toFixed(0) || 0} &euro;</p></CardContent></Card>
              <Card className="card-surface"><CardContent className="pt-4"><p className="text-xs text-slate-500 uppercase font-semibold">Reste</p><p className={`text-2xl font-bold ${Number(budgetSummary.total_budget_remaining||0)<0?'text-red-600':'text-emerald-700'}`}>{budgetSummary.total_budget_remaining?.toFixed(0) || 0} &euro;</p></CardContent></Card>
              <Card className="card-surface"><CardContent className="pt-4"><p className="text-xs text-slate-500 uppercase font-semibold">Prévision fin</p><p className="text-2xl font-bold text-slate-900">{budgetSummary.total_budget_forecast?.toFixed(0) || 0} &euro;</p></CardContent></Card>
            </div>
          )}

          {budgetVentilation?.rows?.length>0&&<Card className="card-surface"><CardHeader className="pb-2"><CardTitle className="text-base font-heading">Ventilation commandes / encours</CardTitle><p className="text-xs text-slate-500">Commandé ventilé sur la date du document ; encours à facturer/livrer ventilés sur la date de livraison prévue.</p></CardHeader><CardContent className="p-0 overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Mois</TableHead><TableHead className="text-right">Commandé</TableHead><TableHead className="text-right">Reste à facturer</TableHead><TableHead className="text-right">Reste à livrer</TableHead><TableHead className="text-right">Prévision encours</TableHead></TableRow></TableHeader><TableBody>{budgetVentilation.rows.map(r=><TableRow key={r.month}><TableCell className="font-semibold">{new Date(`${r.month}-01T12:00:00`).toLocaleDateString('fr-FR',{month:'long',year:'numeric'})}</TableCell><TableCell className="text-right">{Number(r.ordered||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} €</TableCell><TableCell className="text-right">{Number(r.remaining_invoice||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} €</TableCell><TableCell className="text-right">{Number(r.remaining_delivery||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} €</TableCell><TableCell className="text-right font-semibold">{Number(r.forecast_delivery||0).toLocaleString('fr-FR',{minimumFractionDigits:2})} €</TableCell></TableRow>)}</TableBody></Table></CardContent></Card>}

          {/* Budget Lines */}
          <Card className="card-surface">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading">Lignes de Budget</CardTitle>
                <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="btn-primary" data-testid="add-budget-btn"><Plus size={14} className="mr-1" />Ajouter</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Nouvelle ligne budgétaire</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-3">
                      <div className="space-y-1.5">
                        <Label>Nature *</Label>
                        <Select value={newBudget.category} onValueChange={v => setNewBudget(p => ({ ...p, category: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{(budgetNatures?.length?budgetNatures:[{code:'service',label:'Prestation'},{code:'material',label:'Matériel'},{code:'license',label:'Licence / logiciel'},{code:'other',label:'Autre'}]).map(x=><SelectItem key={x.code} value={x.code}>{x.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Libelle *</Label>
                        <Input value={newBudget.label} onChange={e => setNewBudget(p => ({ ...p, label: e.target.value }))} placeholder="Ex: Dev module X" data-testid="budget-label-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>Date d’effet</Label><Input type="date" value={newBudget.effective_date||''} onChange={e=>setNewBudget(p=>({...p,effective_date:e.target.value}))}/></div>
                        <div className="space-y-1.5"><Label>Type</Label><Select value={newBudget.line_type} onValueChange={v=>setNewBudget(p=>({...p,line_type:v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="adjustment">Réajustement budget</SelectItem><SelectItem value="manual">Engagement manuel</SelectItem><SelectItem value="invoice">Facture</SelectItem><SelectItem value="credit_note">Avoir</SelectItem></SelectContent></Select></div>
                        <div className="space-y-1.5"><Label>Statut</Label><Select value={newBudget.status} onValueChange={v=>setNewBudget(p=>({...p,status:v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="draft">Brouillon</SelectItem><SelectItem value="pending">À valider</SelectItem><SelectItem value="validated">Validé</SelectItem><SelectItem value="applied">Appliqué</SelectItem></SelectContent></Select></div>
                        {newBudget.line_type==='adjustment' ? <div className="space-y-1.5 col-span-2"><Label>Réajustement (+ / - €)</Label><Input type="number" step="0.01" value={newBudget.adjustment_amount} onChange={e=>setNewBudget(p=>({...p,adjustment_amount:e.target.value}))}/><p className="text-[11px] text-slate-500">Seuls les réajustements Validé/Appliqué modifient le budget en cours.</p></div> : <>
                        <div className="space-y-1.5"><Label>Engagé (&euro;)</Label><Input type="number" step="0.01" value={newBudget.engaged_amount} onChange={e => setNewBudget(p => ({ ...p, engaged_amount: e.target.value }))} /></div>
                        <div className="space-y-1.5"><Label>Réalisé (&euro;)</Label><Input type="number" step="0.01" value={newBudget.consumed_amount} onChange={e => setNewBudget(p => ({ ...p, consumed_amount: e.target.value }))} /></div></>}
                        <div className="space-y-1.5 col-span-2"><Label>Prévision fin (&euro;)</Label><Input type="number" step="0.01" value={newBudget.forecast_amount} onChange={e => setNewBudget(p => ({ ...p, forecast_amount: e.target.value }))} /></div>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Description</Label>
                        <Textarea value={newBudget.description} onChange={e => setNewBudget(p => ({ ...p, description: e.target.value }))} rows={2} />
                      </div>
                      <div className="flex justify-end gap-3 pt-2">
                        <Button variant="outline" onClick={() => setShowBudgetDialog(false)}>Annuler</Button>
                        <Button className="btn-primary" data-testid="submit-budget-btn" disabled={!newBudget.label}
                          onClick={async () => {
                            try {
                              await budgetsApi.createLine({ ...newBudget, project_id: projectId, effective_date: newBudget.effective_date ? new Date(`${newBudget.effective_date}T12:00:00`).toISOString() : null, adjustment_amount: parseFloat(newBudget.adjustment_amount)||0, estimated_amount: parseFloat(newBudget.estimated_amount)||0, engaged_amount: parseFloat(newBudget.engaged_amount)||0, consumed_amount: parseFloat(newBudget.consumed_amount)||0, forecast_amount: newBudget.forecast_amount === '' ? null : (parseFloat(newBudget.forecast_amount)||0) });
                              toast.success('Ligne ajoutee');
                              setShowBudgetDialog(false);
                              setNewBudget({ category: 'service', line_type:'adjustment', status:'draft', effective_date:new Date().toISOString().slice(0,10), label:'', adjustment_amount:'', estimated_amount:'0', engaged_amount:'0', consumed_amount:'0', forecast_amount:'', description:'' });
                              fetchData();
                            } catch (e) { toast.error('Erreur'); }
                          }}>
                          Creer
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {budgetLines.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucune ligne de budget</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Nature / Type</TableHead>
                      <TableHead className="font-semibold">Libelle</TableHead>
                      <TableHead className="font-semibold">Commande / Ajust.</TableHead>
                      <TableHead className="font-semibold">Engagé</TableHead>
                      <TableHead className="font-semibold">Réalisé</TableHead>
                      <TableHead className="font-semibold">Prévision</TableHead>
                      <TableHead className="font-semibold">Encours</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {budgetLines.map(bl => {
                      const reste = bl.line_type==='purchase_order' ? Number(bl.remaining_invoice_value||0) : (bl.line_type==='adjustment' ? Number(bl.adjustment_amount||0) : Number(bl.engaged_amount||0)-Number(bl.consumed_amount||0));
                      return (
                        <TableRow key={bl.id} data-testid={`budget-line-${bl.id}`}>
                          <TableCell><div className="space-y-1"><Badge variant="outline" className="capitalize text-xs">{bl.category}</Badge><div className="text-[10px] text-slate-500">{bl.line_type==='purchase_order'?'Commande achat':bl.line_type==='adjustment'?'Réajustement':bl.line_type}</div></div></TableCell>
                          <TableCell className="font-medium"><div>{bl.label}</div>{bl.purchase_order&&<div className="text-[10px] text-slate-500">BC {bl.purchase_order} / {bl.purchase_item} · Ordre {bl.accounting_order}</div>}</TableCell>
                          <TableCell className="font-mono">{Number(bl.line_type==='adjustment'?bl.adjustment_amount:(bl.engaged_amount||bl.estimated_amount||0)).toFixed(2)} &euro;</TableCell>
                          <TableCell className="font-mono">{Number(bl.engaged_amount||0).toFixed(2)} &euro;</TableCell>
                          <TableCell className="font-mono">{Number(bl.consumed_amount||0).toFixed(2)} &euro;</TableCell>
                          <TableCell className="font-mono">{Number(bl.forecast_amount ?? bl.estimated_amount ?? 0).toFixed(2)} &euro;</TableCell>
                          <TableCell className={`font-mono font-bold ${reste >= 0 ? 'text-green-600' : 'text-red-600'}`}>{reste.toFixed(2)} &euro;{bl.line_type==='purchase_order'&&<div className="text-[10px] font-normal text-slate-500">À livrer {Number(bl.remaining_delivery_value||0).toFixed(2)} €</div>}</TableCell>
                          <TableCell className="text-right"><div className="flex items-center justify-end gap-1"><Select onValueChange={async docId=>{ try { await budgetsApi.attachDocument(bl.id,docId); toast.success('Justificatif rattaché'); fetchData(); } catch(e){ toast.error(e.response?.data?.detail||'Erreur justificatif'); } }}><SelectTrigger className="h-7 w-32 text-[10px]"><SelectValue placeholder={`Pièce ${(bl.document_ids||[]).length?`(${bl.document_ids.length})`:''}`}/></SelectTrigger><SelectContent>{documents.map(d=><SelectItem key={d.id} value={d.id}>{d.original_name}</SelectItem>)}</SelectContent></Select>
                            <Select value={budgetDocCategories[bl.id]||'budget'} onValueChange={v=>setBudgetDocCategories(p=>({...p,[bl.id]:v}))}><SelectTrigger className="h-7 w-32 text-[10px]"><SelectValue/></SelectTrigger><SelectContent>{(documentCategories?.length?documentCategories:[{code:'budget',label:'Budget'},{code:'other',label:'Autre'}]).map(x=><SelectItem key={x.code} value={x.code}>{x.label}</SelectItem>)}</SelectContent></Select><label className="inline-flex"><input type="file" className="hidden" onChange={async e=>{const f=e.target.files?.[0];if(!f)return;const cat=budgetDocCategories[bl.id]||'budget';try{await budgetsApi.uploadLineDocument(bl.id,f,cat);toast.success('Justificatif ajouté au budget et aux documents du projet');fetchData()}catch(err){toast.error(err.response?.data?.detail||'Erreur justificatif')}finally{e.target.value=''}}}/><Button type="button" variant="ghost" size="sm" className="h-7 px-2" asChild><span><Upload size={13} className="mr-1"/>Fichier</span></Button></label><Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={async()=>{const url=window.prompt('Lien Google Drive / OneDrive / SharePoint / web');if(!url)return;const name=window.prompt('Libellé du document','Justificatif budget');if(!name)return;const category=budgetDocCategories[bl.id]||'budget';try{await budgetsApi.addLineDocumentLink(bl.id,{url,name,category});toast.success('Lien ajouté au budget et aux documents du projet');fetchData()}catch(err){toast.error(err.response?.data?.detail||'Erreur lien')}}}><LinkSimple size={13} className="mr-1"/>Lien</Button><Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" data-testid={`delete-budget-${bl.id}`}
                              onClick={async () => { await budgetsApi.deleteLine(bl.id); toast.success('Ligne supprimee'); fetchData(); }}>
                              <Trash size={14} />
                            </Button></div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Materials */}
          <Card className="card-surface">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading">Materiel</CardTitle>
                <Dialog open={showMaterialDialog} onOpenChange={setShowMaterialDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="btn-primary" data-testid="add-material-btn"><Plus size={14} className="mr-1" />Ajouter</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Nouveau materiel</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-3">
                      <div className="space-y-1.5">
                        <Label>Nom *</Label>
                        <Input value={newMaterial.name} onChange={e => setNewMaterial(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Serveur Dell R740" data-testid="material-name-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label>Categorie</Label>
                          <Select value={newMaterial.category} onValueChange={v => setNewMaterial(p => ({ ...p, category: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="serveur">Serveur</SelectItem>
                              <SelectItem value="poste">Poste de travail</SelectItem>
                              <SelectItem value="licence">Licence</SelectItem>
                              <SelectItem value="reseau">Reseau</SelectItem>
                              <SelectItem value="autre">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label>Statut</Label>
                          <Select value={newMaterial.status} onValueChange={v => setNewMaterial(p => ({ ...p, status: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="demande">Demande</SelectItem>
                              <SelectItem value="commande">Commande</SelectItem>
                              <SelectItem value="livre">Livre</SelectItem>
                              <SelectItem value="installe">Installe</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label>Quantite</Label>
                          <Input type="number" min="1" value={newMaterial.quantity} onChange={e => setNewMaterial(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Cout unitaire (&euro;)</Label>
                          <Input type="number" step="0.01" value={newMaterial.unit_cost} onChange={e => setNewMaterial(p => ({ ...p, unit_cost: e.target.value }))} />
                        </div>
                      </div>
                      <div className="flex justify-end gap-3 pt-2">
                        <Button variant="outline" onClick={() => setShowMaterialDialog(false)}>Annuler</Button>
                        <Button className="btn-primary" data-testid="submit-material-btn" disabled={!newMaterial.name}
                          onClick={async () => {
                            try {
                              await budgetsApi.createMaterial({ ...newMaterial, project_id: projectId, unit_cost: parseFloat(newMaterial.unit_cost) || 0 });
                              toast.success('Materiel ajoute');
                              setShowMaterialDialog(false);
                              setNewMaterial({ name: '', category: 'serveur', quantity: 1, unit_cost: '', status: 'demande', description: '' });
                              fetchData();
                            } catch (e) { toast.error('Erreur'); }
                          }}>
                          Creer
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {materials.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucun materiel</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Nom</TableHead>
                      <TableHead className="font-semibold">Categorie</TableHead>
                      <TableHead className="font-semibold">Qte</TableHead>
                      <TableHead className="font-semibold">Cout unit.</TableHead>
                      <TableHead className="font-semibold">Total</TableHead>
                      <TableHead className="font-semibold">Statut</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materials.map(mat => (
                      <TableRow key={mat.id} data-testid={`material-${mat.id}`}>
                        <TableCell className="font-medium">{mat.name}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize text-xs">{mat.category}</Badge></TableCell>
                        <TableCell>{mat.quantity}</TableCell>
                        <TableCell className="font-mono">{mat.unit_cost?.toFixed(2)} &euro;</TableCell>
                        <TableCell className="font-mono font-bold">{(mat.quantity * mat.unit_cost).toFixed(2)} &euro;</TableCell>
                        <TableCell>
                          <Select value={mat.status} onValueChange={async (v) => { await budgetsApi.updateMaterial(mat.id, { status: v }); toast.success('Statut mis a jour'); fetchData(); }}>
                            <SelectTrigger className="w-28 h-7 text-xs" onClick={e => e.stopPropagation()}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="demande">Demande</SelectItem>
                              <SelectItem value="commande">Commande</SelectItem>
                              <SelectItem value="livre">Livre</SelectItem>
                              <SelectItem value="installe">Installe</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" data-testid={`delete-material-${mat.id}`}
                            onClick={async () => { await budgetsApi.deleteMaterial(mat.id); toast.success('Materiel supprime'); fetchData(); }}>
                            <Trash size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Team */}
      {project.assigned_users_details?.length > 0 && (
        <Card className="card-surface">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users size={16} className="text-blue-600" />Équipe projet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {project.assigned_users_details.map(u => (
                <div key={u.id} className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
                  <Avatar className="w-6 h-6"><AvatarFallback className="text-[10px] bg-blue-100 text-blue-700">{getInitials(u.name)}</AvatarFallback></Avatar>
                  <div>
                    <p className="text-xs font-medium">{u.name}</p>
                    <p className="text-[10px] text-slate-500">{getLabel(u.role, 'role')}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Requirement Dialog */}
      <Dialog open={!!editingReq} onOpenChange={() => setEditingReq(null)}>
        <DialogContent data-testid="edit-req-dialog">
          <DialogHeader><DialogTitle>Modifier l'exigence</DialogTitle></DialogHeader>
          {editingReq && (
            <div className="space-y-3 mt-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Titre *</Label>
                <Input value={editingReq.title} onChange={e => setEditingReq(p => ({ ...p, title: e.target.value }))} data-testid="edit-req-title" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description mise en page</Label>
                <ProjectRequirementEditor value={editingReq.description_html || ''} onChange={v => setEditingReq(p => ({ ...p, description_html: v }))} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Type</Label>
                  <Select value={editingReq.type} onValueChange={v => setEditingReq(p => ({ ...p, type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="business">Metier</SelectItem>
                      <SelectItem value="functional">Fonctionnelle</SelectItem>
                      <SelectItem value="technical">Technique</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Priorite</Label>
                  <Select value={editingReq.priority} onValueChange={v => setEditingReq(p => ({ ...p, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Basse</SelectItem>
                      <SelectItem value="medium">Moyenne</SelectItem>
                      <SelectItem value="high">Haute</SelectItem>
                      <SelectItem value="critical">Critique</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Statut</Label>
                  <Select value={editingReq.status} onValueChange={v => setEditingReq(p => ({ ...p, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REQUIREMENT_STATUSES.map(st => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setEditingReq(null)}>Annuler</Button>
                <Button className="btn-primary" size="sm" onClick={handleUpdateReq} disabled={!editingReq.title} data-testid="save-req-btn">Enregistrer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Milestone Dialog (from project) */}
      <Dialog open={!!editingMs} onOpenChange={() => setEditingMs(null)}>
        <DialogContent data-testid="edit-ms-dialog">
          <DialogHeader><DialogTitle>Modifier le jalon</DialogTitle></DialogHeader>
          {editingMs && (
            <div className="space-y-3 mt-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Nom *</Label>
                <Input value={editingMs.name} onChange={e => setEditingMs(p => ({ ...p, name: e.target.value }))} data-testid="edit-ms-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description</Label>
                <Textarea value={editingMs.description || ''} onChange={e => setEditingMs(p => ({ ...p, description: e.target.value }))} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Date cible</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-xs h-9">
                        <CalendarBlank size={12} className="mr-1" />{format(new Date(editingMs.target_date), 'dd/MM/yy', { locale: fr })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={new Date(editingMs.target_date)} onSelect={d => d && setEditingMs(p => ({ ...p, target_date: d }))} locale={fr} /></PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Statut</Label>
                  <Select value={editingMs.status || 'pending'} onValueChange={v => setEditingMs(p => ({ ...p, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="achieved">Atteint</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setEditingMs(null)}>Annuler</Button>
                <Button className="bg-orange-600 hover:bg-orange-700" size="sm" onClick={handleUpdateMs} disabled={!editingMs.name} data-testid="save-ms-btn">Enregistrer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog - V7.0.0.2 : écran compact, délai distinct de la charge */}
      <Dialog open={showEditProject} onOpenChange={setShowEditProject}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="edit-project-dialog">
          <DialogHeader><DialogTitle className="text-xl">Modifier le projet</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Mettez à jour les informations, le délai et le pilotage du projet.</p>

          <div className="grid lg:grid-cols-[.65fr_1.15fr_.65fr_.75fr_.55fr] gap-3 mt-2">
            <div><Label className="text-xs">Code</Label><Input value={editProject.code||''} disabled className="h-10 bg-slate-50"/></div>
            <div><Label className="text-xs">Nom *</Label><Input value={editProject.name||''} onChange={e=>setEditProject(p=>({...p,name:e.target.value}))} className="h-10 font-medium" data-testid="edit-project-name"/></div>
            <div><Label className="text-xs">Statut</Label><Select value={editProject.status||'draft'} onValueChange={v=>{setEditProject(p=>({...p,status:v}));setClosureCheck(null);}}><SelectTrigger className="h-10"><SelectValue/></SelectTrigger><SelectContent>{PROJECT_STATUSES.map(st=><SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Service</Label><Select value={editProject.service_id||'none'} onValueChange={v=>setEditProject(p=>({...p,service_id:v}))}><SelectTrigger className="h-10"><SelectValue placeholder="Aucun"/></SelectTrigger><SelectContent><SelectItem value="none">Aucun</SelectItem>{services.map(x=><SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Progression</Label><div className="h-10 rounded-lg border bg-slate-50 flex items-center gap-2 px-3"><div className="h-2 flex-1 rounded bg-slate-200 overflow-hidden"><div className="h-full bg-emerald-500" style={{width:`${Math.max(0,Math.min(100,progress))}%`}}/></div><b className="text-xs">{progress}%</b></div></div>
          </div>

          <Tabs defaultValue="general" className="mt-2 space-y-4">
            <TabsList className="w-full justify-start bg-transparent border-b rounded-none h-auto p-0">
              <TabsTrigger value="general" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Général</TabsTrigger>
              <TabsTrigger value="delay" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Délais & charge</TabsTrigger>
              <TabsTrigger value="team" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Équipe</TabsTrigger>
              <TabsTrigger value="pilot" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Pilotage</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-3">
              <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 text-xs text-blue-800"><b>Le délai et la charge sont deux notions différentes.</b> Les dates définissent la fenêtre du projet. La charge est exprimée exclusivement en J/JH.</div>
              <div className="grid lg:grid-cols-2 gap-3">
                <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-3"><Folders size={18} className="text-blue-600"/><div><b>Contexte</b><p className="text-[11px] text-slate-500">Client et service concernés.</p></div></div><div className="grid grid-cols-2 gap-3"><div><Label>Client</Label><Select value={editProject.client_id||'none'} onValueChange={v=>setEditProject(p=>({...p,client_id:v}))}><SelectTrigger><SelectValue placeholder="Aucun"/></SelectTrigger><SelectContent><SelectItem value="none">Aucun</SelectItem>{clients.map(c=><SelectItem key={c.id} value={c.id}>{c.code} - {c.name}</SelectItem>)}</SelectContent></Select></div><div><Label>Service</Label><Select value={editProject.service_id||'none'} onValueChange={v=>setEditProject(p=>({...p,service_id:v}))}><SelectTrigger><SelectValue placeholder="Aucun"/></SelectTrigger><SelectContent><SelectItem value="none">Aucun</SelectItem>{services.map(x=><SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div></div></div>
                <div className="rounded-xl border bg-white p-4"><div className="flex items-center justify-between gap-3"><div><div className="flex items-center gap-2"><Warning size={18} className="text-blue-600"/><b>Notifications client</b></div><p className="text-[11px] text-slate-500 mt-1">Notifier les clients internes lors des changements importants.</p></div><label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked={editProject.notify_client!==false} onCheckedChange={v=>setEditProject(p=>({...p,notify_client:!!v}))}/>Actives</label></div></div>
              </div>
              <div className="rounded-xl border bg-white p-4"><div className="grid md:grid-cols-2 gap-3"><div><Label>N° d'ordre comptable</Label><Input value={editProject.accounting_order||''} onChange={e=>setEditProject(p=>({...p,accounting_order:e.target.value}))} placeholder="Ex: 1000067"/><p className="text-[11px] text-slate-500 mt-1">Clé de rapprochement des commandes/factures importées.</p></div><div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600"><b>Rapprochement comptable</b><p className="mt-1">Ordre + N° commande + poste permettent de rattacher et mettre à jour les lignes d'achat sans doublon.</p></div></div></div>
              <div className="rounded-xl border bg-white p-4"><Label>Description</Label><Textarea value={editProject.description||''} onChange={e=>setEditProject(p=>({...p,description:e.target.value}))} rows={4} placeholder="Description du projet..." data-testid="edit-project-description"/></div>
            </TabsContent>

            <TabsContent value="delay" className="space-y-3">
              <div className="grid lg:grid-cols-[1.15fr_.8fr_.7fr] gap-3">
                <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><CalendarBlank size={19} className="text-blue-600"/><div><b>Fenêtre du projet</b><p className="text-[11px] text-slate-500">Période de réalisation du projet.</p></div></div><div className="grid grid-cols-2 gap-3"><div><Label>Date début</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{editProject.start_date?format(new Date(editProject.start_date),'dd/MM/yyyy',{locale:fr}):'-'}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={editProject.start_date?new Date(editProject.start_date):undefined} onSelect={d=>d&&setEditProject(p=>({...p,start_date:d}))} locale={fr}/></PopoverContent></Popover></div><div><Label>Date fin</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{editProject.end_date?format(new Date(editProject.end_date),'dd/MM/yyyy',{locale:fr}):'Non définie'}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={editProject.end_date?new Date(editProject.end_date):undefined} onSelect={d=>setEditProject(p=>({...p,end_date:d||null}))} locale={fr}/></PopoverContent></Popover></div></div></div>
                <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><Target size={19} className="text-indigo-600"/><div><b>Pilotage</b><p className="text-[11px] text-slate-500">Charge et budget du projet.</p></div></div><div className="grid grid-cols-2 gap-3"><div><Label>Charge cible (J)</Label><Input type="number" step="0.01" min="0" value={editProject.estimated_days??''} onChange={e=>setEditProject(p=>({...p,estimated_days:e.target.value}))} className="font-semibold" data-testid="edit-project-days"/></div><div><Label>Budget</Label><Input type="number" value={editProject.budget??''} onChange={e=>setEditProject(p=>({...p,budget:e.target.value}))} data-testid="edit-project-budget"/></div></div></div>
                <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-3"><Clock size={18} className="text-emerald-600"/><b>Suivi de la charge</b></div><div className="space-y-2 text-sm"><div className="flex justify-between"><span>Charge cible</span><b>{Number(editProject.estimated_days||0).toFixed(2)} J</b></div><div className="flex justify-between"><span>Charge affectée</span><b className="text-emerald-700">{Number(project.resource_allocated_days||0).toFixed(2)} J</b></div><div className="flex justify-between"><span>Reste à affecter</span><b>{Math.max(0,Number(editProject.estimated_days||0)-Number(project.resource_allocated_days||0)).toFixed(2)} J</b></div></div></div>
              </div>
            </TabsContent>

            <TabsContent value="team" className="space-y-3">
              <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-3"><Users size={19} className="text-blue-600"/><div><b>Équipe projet</b><p className="text-[11px] text-slate-500">Membres pouvant être affectés aux tâches du projet.</p></div></div><div className="flex flex-wrap gap-2">{users.map(u=><button key={u.id} type="button" onClick={()=>toggleEditUserAssignment(u.id)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${editProject.assigned_users?.includes(u.id)?'bg-blue-50 text-blue-700 border-blue-300':'bg-white text-slate-600 hover:bg-slate-50'}`}><Avatar className="w-6 h-6"><AvatarFallback className="text-[9px]">{getInitials(u.name)}</AvatarFallback></Avatar>{u.name}</button>)}</div></div>
            </TabsContent>

            <TabsContent value="pilot" className="space-y-3">
              <div className="grid md:grid-cols-3 gap-3"><div className="rounded-xl border bg-white p-4"><div className="text-[11px] text-slate-500 uppercase">Charge cible</div><div className="text-2xl font-bold mt-1">{Number(editProject.estimated_days||0).toFixed(2)} J</div></div><div className="rounded-xl border bg-white p-4"><div className="text-[11px] text-slate-500 uppercase">Charge des tâches</div><div className="text-2xl font-bold mt-1">{Number(project.tasks_estimated_days||0).toFixed(2)} J</div></div><div className="rounded-xl border bg-white p-4"><div className="text-[11px] text-slate-500 uppercase">Charge positionnée</div><div className="text-2xl font-bold mt-1">{Number(project.scheduled_days||0).toFixed(2)} J</div></div></div>
              {editProject.status==='closed'&&<div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"><div className="flex items-center justify-between gap-3"><div><b>Assistant de clôture</b><div>Contrôle les tâches ouvertes, temps non validés, risques, jalons et réunions futures.</div></div><Button type="button" variant="outline" size="sm" onClick={analyzeProjectClosure} disabled={closureLoading}>{closureLoading?'Analyse...':'Analyser'}</Button></div>{closureCheck&&<div className="bg-white rounded border p-2 mt-2"><b className={closureCheck.can_close?'text-emerald-700':'text-red-700'}>{closureCheck.can_close?'Projet clôturable':'Clôture bloquée'}</b><div className="mt-1">{closureCheck.open_tasks||0} tâche(s) ouverte(s) · {closureCheck.unvalidated_time||0} temps non validé(s) · {closureCheck.open_risks||0} risque(s) · {closureCheck.pending_milestones||0} jalon(s) · {closureCheck.future_meetings||0} réunion(s) future(s)</div></div>}</div>}
            </TabsContent>
          </Tabs>

          <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 border-t bg-white/95 backdrop-blur flex justify-end gap-2"><Button variant="outline" onClick={()=>setShowEditProject(false)}>Annuler</Button><Button className="btn-primary" onClick={handleUpdateProject} disabled={!editProject.name} data-testid="save-project-btn">Enregistrer</Button></div>
        </DialogContent>
      </Dialog>

      {/* Google Agenda - planification d'une reunion du projet */}
      <Dialog open={googleMeetingOpen} onOpenChange={setGoogleMeetingOpen}>
        <DialogContent className="max-w-lg" data-testid="google-project-meeting-dialog">
          <DialogHeader><DialogTitle>Planifier une réunion dans Google Agenda</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="rounded-lg border bg-blue-50 px-3 py-2 text-xs text-blue-800">
              Projet <strong>{project.code}</strong> — l'événement portera automatiquement le code projet pour permettre la synchronisation vers la gestion des temps.
            </div>
            <div className="space-y-1"><Label>Titre</Label><Input value={googleMeetingForm.title} onChange={e=>setGoogleMeetingForm(p=>({...p,title:e.target.value}))} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Début</Label><Input type="datetime-local" value={googleMeetingForm.start} onChange={e=>setGoogleMeetingForm(p=>({...p,start:e.target.value}))} /></div>
              <div className="space-y-1"><Label>Fin</Label><Input type="datetime-local" value={googleMeetingForm.end} onChange={e=>setGoogleMeetingForm(p=>({...p,end:e.target.value}))} /></div>
            </div>
            <div className="space-y-1"><Label>Participants (emails, séparés par ;)</Label><Input value={googleMeetingForm.attendees} onChange={e=>setGoogleMeetingForm(p=>({...p,attendees:e.target.value}))} placeholder="prenom.nom@entreprise.fr; ..." /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea rows={3} value={googleMeetingForm.description} onChange={e=>setGoogleMeetingForm(p=>({...p,description:e.target.value}))} /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={()=>setGoogleMeetingOpen(false)}>Annuler</Button><Button className="btn-primary" onClick={createGoogleProjectMeeting}>Créer dans Google Agenda</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={!!editingTask} onOpenChange={() => setEditingTask(null)}>
        <DialogContent className="dialog-wide max-w-6xl h-[88vh] max-h-[88vh] overflow-hidden flex flex-col" data-testid="edit-task-project-dialog">
          <DialogHeader>
            <DialogTitle>Modifier la tache</DialogTitle>
          </DialogHeader>
          {editingTask && (
            <EditTaskFormProject
              task={editingTask}
              users={users}
              allTasks={tasks}
              sprints={sprints}
              projectId={projectId}
              onSave={async (updatedData, planning) => {
                try {
                  const oldAssigned=new Set(Array.isArray(editingTask.assigned_to)?editingTask.assigned_to:(editingTask.assigned_to?[editingTask.assigned_to]:[]));
                  const newAssigned=new Set(updatedData.assigned_to||[]);
                  const removed=[...oldAssigned].filter(x=>!newAssigned.has(x));
                  const oldStart=String(editingTask.start_date||'').slice(0,10); const oldEnd=String(editingTask.end_date||'').slice(0,10);
                  const newStart=String(updatedData.start_date||'').slice(0,10); const newEnd=String(updatedData.end_date||'').slice(0,10);
                  const shrinking=!!oldStart&&!!oldEnd&&((newStart&&newStart>oldStart)||(newEnd&&newEnd<oldEnd));
                  if (planning && (removed.length||shrinking)) {
                    const transition={...planning,allocations:(planning.allocations||[]).filter(a=>oldAssigned.has(a.user_id)).map(a=>({...a,days:(a.days||[]).filter(d=>(!oldStart||d.date>=oldStart)&&(!oldEnd||d.date<=oldEnd))}))};
                    await taskPlanningApi.save(editingTask.id,transition);
                  }
                  await tasksApi.update(editingTask.id, updatedData);
                  if (planning) await taskPlanningApi.save(editingTask.id,planning);
                  toast.success('Tache et charge mises a jour');
                  setEditingTask(null);
                  fetchData();
                } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
              }}
              onDelete={async () => {
                try {
                  await tasksApi.delete(editingTask.id);
                  toast.success('Tache supprimee');
                  setEditingTask(null);
                  fetchData();
                } catch { toast.error('Erreur'); }
              }}
              onGoogleCalendar={googleStatus.enabled ? (() => addTaskToGoogleCalendar(editingTask)) : null}
              onCancel={() => setEditingTask(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const TASK_STATUSES_EDIT = [
  { value: 'todo', label: 'A faire' },
  { value: 'analysis', label: 'Analyse' },
  { value: 'request', label: 'Demande' },
  { value: 'development', label: 'Developpement' },
  { value: 'deliverable', label: 'Livrable' },
  { value: 'testing', label: 'Recette' },
  { value: 'production', label: 'Production' },
  { value: 'closed', label: 'Cloture' },
];

function EditTaskFormProject({ task, users, allTasks, sprints = [], projectId, onSave, onDelete, onGoogleCalendar, onCancel }) {
  const [planningPayload, setPlanningPayload] = useState(null);
  const [workflow,setWorkflow]=useState({transitions:[]});
  const [statusReason,setStatusReason]=useState('');
  useEffect(()=>{tasksApi.getWorkflow().then(r=>setWorkflow(r.data||{transitions:[]})).catch(()=>{})},[]);
  const [form, setForm] = useState({
    title: task.title || '', description: task.description || '',
    start_date: task.start_date ? new Date(task.start_date) : new Date(),
    end_date: task.end_date ? new Date(task.end_date) : addDays(new Date(), 7),
    estimated_days: task.estimated_days != null ? String(task.estimated_days) : '',
    status: task.status || 'todo', priority: task.priority || 'medium', priority_level: task.priority_level ?? 2,
    position: task.position ?? 1,
    assigned_to: Array.isArray(task.assigned_to) ? task.assigned_to : (task.assigned_to ? [task.assigned_to] : []),
    parent_task_id: task.parent_task_id || '', sprint_id: task.sprint_id || '',
    dependencies: Array.isArray(task.dependencies) ? task.dependencies : [], progress: task.progress != null ? task.progress : 0,
    self_assignment_enabled: !!task.self_assignment_enabled,
  });
  const parentTasks = allTasks.filter(t => !t.parent_task_id && t.id !== task.id);
  const subtasks = allTasks.filter(t => t.parent_task_id === task.id);
  const allowedStatusValues = [task.status, ...((workflow.transitions||[]).filter(r=>r.from===task.status).map(r=>r.to))];
  const availableStatuses = TASK_STATUSES_EDIT.filter(x=>allowedStatusValues.includes(x.value));
  const changeStatus = (value) => {
    const rule=(workflow.transitions||[]).find(r=>r.from===task.status&&r.to===value);
    if(rule?.backward==='justification'){ const reason=window.prompt('Justification obligatoire pour ce retour en arrière'); if(!reason)return; setStatusReason(reason); } else setStatusReason('');
    setForm(p=>({...p,status:value}));
  };
  const handleSubmit = () => onSave({
    title: form.title, description: form.description,
    start_date: toNoonUTC(form.start_date), end_date: toNoonUTC(form.end_date),
    estimated_days: form.estimated_days !== '' ? parseFloat(form.estimated_days) : null,
    status: form.status, priority: form.priority, priority_level: parseInt(form.priority_level ?? 2),
    position: parseInt(form.position ?? 1), assigned_to: form.assigned_to, progress: form.progress,
    parent_task_id: form.parent_task_id || null, sprint_id: form.sprint_id || null,
    dependencies: form.dependencies || [], self_assignment_enabled: !!form.self_assignment_enabled, status_change_reason: statusReason,
  }, planningPayload);

  return <div className="flex-1 min-h-0 flex flex-col">
    <div className="grid lg:grid-cols-[1.4fr_.55fr_.45fr_.55fr] gap-3 mb-4">
      <div><Label className="text-xs">Titre *</Label><Input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} className="h-10 font-medium" data-testid="edit-task-title-proj"/></div>
      <div><Label className="text-xs">Statut</Label><Select value={form.status} onValueChange={changeStatus}><SelectTrigger className="h-10"><SelectValue/></SelectTrigger><SelectContent>{availableStatuses.map(x=><SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent></Select></div>
      <div><Label className="text-xs">Priorité</Label><Input type="number" min="0" step="1" value={form.priority_level} onChange={e=>setForm(p=>({...p,priority_level:e.target.value}))} className="h-10"/></div>
      <div><Label className="text-xs">Progression</Label><div className="h-10 rounded-lg border bg-slate-50 flex items-center gap-2 px-3"><div className="h-2 flex-1 rounded bg-slate-200 overflow-hidden"><div className="h-full bg-emerald-500" style={{width:`${Math.max(0,Math.min(100,form.progress))}%`}}/></div><b className="text-xs">{form.progress}%</b></div></div>
    </div>

    <Tabs defaultValue="general" className="flex-1 min-h-0 flex flex-col">
      <TabsList className="w-full justify-start bg-transparent border-b rounded-none h-auto p-0"><TabsTrigger value="general" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Général</TabsTrigger><TabsTrigger value="delay" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Délais & charge</TabsTrigger><TabsTrigger value="resources" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Ressources</TabsTrigger><TabsTrigger value="deps" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:shadow-none">Dépendances</TabsTrigger></TabsList>

      <TabsContent value="general" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3"><div className="grid lg:grid-cols-[1.3fr_.45fr] gap-3"><div className="rounded-xl border bg-white p-4"><Label>Description</Label><Textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={5} placeholder="Description de la tâche..."/></div><div className="rounded-xl border bg-white p-4 space-y-3"><div><Label>Position</Label><Input type="number" min="1" step="1" value={form.position} onChange={e=>setForm(p=>({...p,position:e.target.value}))}/></div><div className="rounded-lg bg-blue-50 p-3 flex items-center justify-between gap-3"><div><b className="text-xs">Auto-affectation</b><p className="text-[10px] text-slate-500">L'équipe peut se positionner.</p></div><Checkbox checked={form.self_assignment_enabled} onCheckedChange={v=>setForm(p=>({...p,self_assignment_enabled:!!v}))}/></div></div></div>{subtasks.length>0&&<div className="rounded-xl border bg-blue-50 p-3 text-xs text-blue-700"><b>{subtasks.length} sous-tâche(s)</b>{subtasks.map(st=><div key={st.id} className="mt-1">- {st.title}</div>)}</div>}</TabsContent>

      <TabsContent value="delay" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3"><div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 text-xs text-blue-800"><b>Délais et charge sont séparés.</b> Les dates définissent uniquement la fenêtre autorisée. La charge est exprimée en J/JH.</div><div className="grid lg:grid-cols-[1.2fr_.6fr] gap-3"><div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><CalendarBlank size={19} className="text-blue-600"/><b>Fenêtre de réalisation</b></div><div className="grid grid-cols-2 gap-3"><div><Label>Date début</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{format(form.start_date,'dd/MM/yyyy',{locale:fr})}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={form.start_date} onSelect={d=>d&&setForm(p=>({...p,start_date:d}))} locale={fr}/></PopoverContent></Popover></div><div><Label>Date fin</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarBlank size={15} className="mr-2"/>{format(form.end_date,'dd/MM/yyyy',{locale:fr})}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={form.end_date} onSelect={d=>d&&setForm(p=>({...p,end_date:d}))} locale={fr}/></PopoverContent></Popover></div></div></div><div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 mb-4"><Clock size={19} className="text-indigo-600"/><b>Charge de la tâche</b></div><Label>Charge prévue (J)</Label><Input type="number" min="0" step="0.01" value={form.estimated_days} onChange={e=>setForm(p=>({...p,estimated_days:e.target.value}))} placeholder="Ex : 5,00" className="text-lg font-semibold"/></div></div></TabsContent>

      <TabsContent value="resources" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3"><div className="rounded-xl border bg-white p-4"><div className="flex items-center justify-between gap-3 mb-2"><div><b>Affectation des ressources</b><p className="text-[11px] text-slate-500">Une tâche peut porter plusieurs ressources, avec des charges et des jours différents.</p></div>{form.self_assignment_enabled&&<Badge className="bg-blue-100 text-blue-700">Auto-affectation autorisée</Badge>}</div><MultiUserSelect users={users} selectedIds={form.assigned_to} onChange={ids=>setForm(p=>({...p,assigned_to:ids}))} placeholder="Affecter une ou plusieurs ressources..."/></div><TaskResourcePlanningPanel task={task} assignedIds={form.assigned_to} plannedDays={form.estimated_days} startDate={form.start_date} endDate={form.end_date} workingWeekdays={task.working_weekdays||[0,1,2,3,4]} onPlanChange={setPlanningPayload}/></TabsContent>

      <TabsContent value="deps" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-3"><div className="grid md:grid-cols-2 gap-3"><div className="rounded-xl border bg-white p-4"><Label>Sous-tâche de</Label><Select value={form.parent_task_id||'none'} onValueChange={v=>setForm(p=>({...p,parent_task_id:v==='none'?'':v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="none">Aucune (tâche principale)</SelectItem>{parentTasks.map(t=><SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent></Select></div><div className="rounded-xl border bg-white p-4"><Label>Sprint</Label><Select value={form.sprint_id||'none'} onValueChange={v=>setForm(p=>({...p,sprint_id:v==='none'?'':v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="none">Aucun sprint</SelectItem>{sprints.map(sp=><SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}</SelectContent></Select></div></div><div className="rounded-xl border bg-white p-4"><Label>Dépend de</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start font-normal">{form.dependencies.length?`${form.dependencies.length} dépendance(s)`:'Aucune dépendance'}</Button></PopoverTrigger><PopoverContent className="w-80 p-2"><div className="max-h-52 overflow-y-auto">{allTasks.filter(t=>t.id!==task.id).map(t=><label key={t.id} className="flex gap-2 items-center p-1.5 rounded hover:bg-slate-50 text-xs"><Checkbox checked={form.dependencies.includes(t.id)} onCheckedChange={ok=>setForm(p=>({...p,dependencies:ok?[...p.dependencies,t.id]:p.dependencies.filter(x=>x!==t.id)}))}/><span>{t.parent_task_id?'↳ ':''}{t.title}</span></label>)}</div></PopoverContent></Popover></div></TabsContent>
    </Tabs>

    <div className="mt-4 -mx-6 px-6 py-3 border-t bg-white flex items-center justify-between gap-3"><Button variant="ghost" className="text-red-600" onClick={onDelete} data-testid="delete-task-proj-btn"><Trash size={16} className="mr-1"/>Supprimer</Button><div className="flex gap-2">{onGoogleCalendar&&<Button variant="outline" onClick={onGoogleCalendar}><CalendarBlank size={15} className="mr-1"/>Google Agenda</Button>}<Button variant="outline" onClick={onCancel}>Annuler</Button><Button className="btn-primary" onClick={handleSubmit} disabled={!form.title} data-testid="save-task-proj-btn">Enregistrer</Button></div></div>
  </div>;
}
